import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { QRCodeSVG as QRCode } from "qrcode.react";
import fetchData from "../../functions/fetchData";
import { logout } from "../../utility/auth";
import { getHeaders } from "../../utility/getHeader";
import useBillingDate from "../../hooks/useBillingDate";
import { BillingDateDisplay } from "../../components/BillingDateProvider";
import { Layout as AntLayout, Button, Card, Row, Col, Space, Divider, Badge, Statistic, Menu } from "antd";
import { ShoppingCartOutlined, DeleteOutlined, PrinterOutlined, DollarOutlined, UserOutlined } from "@ant-design/icons";
import "./CashierTicketBilling.css";

export default function CashierTicketBilling() {
  const navigate = useNavigate();
  const { billingDate, isLoading: isLoadingBillingDate } = useBillingDate();
  const [categories, setCategories] = useState([]);
  const [ticketPricing, setTicketPricing] = useState({});
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customerDisplayWindow, setCustomerDisplayWindow] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showFloatingModal, setShowFloatingModal] = useState(false);
  const [counterClosed, setCounterClosed] = useState(false);
  const [counterStatusMessage, setCounterStatusMessage] = useState('');
  const [showCounterClosedModal, setShowCounterClosedModal] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrBillData, setQrBillData] = useState(null);
  const [qrOrderDetails, setQrOrderDetails] = useState(null);
  const [showReprintModal, setShowReprintModal] = useState(false);
  const [savedTickets, setSavedTickets] = useState([]);
  const isSavingBillRef = useRef(false); // Use ref for immediate synchronous access

  // Check if user is cashier
  const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
  const isCashier = userType?.toLowerCase() === 'cashier';
  
  // Get cashier ID for display
  const cashierId = localStorage.getItem('uname') || sessionStorage.getItem('uname') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id');

  // Helper function to generate short ID (max 10 characters)
  const generateShortId = () => {
    const timestamp = Date.now().toString();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    // Take last 6 digits of timestamp + 3 digit random = 9 chars, add prefix 'T' = 10 chars
    return 'T' + timestamp.slice(-6) + random;
  };

  // Helper function to generate short bill ID (max 10 characters)
  const generateShortBillId = () => {
    const timestamp = Date.now().toString();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    // Take last 6 digits of timestamp + 3 digit random = 9 chars, add prefix 'B' = 10 chars
    return 'B' + timestamp.slice(-6) + random;
  };

  const getTicketVerificationValue = (ticketData) => {
    return (
      ticketData?.barcodeValue ||
      ticketData?.verifyBarcode ||
      ticketData?.ticketId ||
      ''
    );
  };

  // Generate QR as a proper data URL. Using async avoids blank/invalid QR payloads.
  const generateQRCodeDataUrl = async (text) => {
    try {
      const value = String(text || '').trim();
      if (!value) return '';

      const QRCode = require('qrcode');
      return await QRCode.toDataURL(value, {
        width: 256, 
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  // State for storing latest closed date
  const [latestClosedDate, setLatestClosedDate] = useState('');

  // Return the next available date (already +1 day from fetchLatestClosedDate)
  const getNextDayDate = () => {
    if (!latestClosedDate) return 'Not Available';
    return latestClosedDate; // This is already +1 day from the latest closed date
  };

  // Fetch latest closed date from day_close_summary and add +1 day
  const fetchLatestClosedDate = useCallback(async () => {
    try {
      const dayCloseSummary = await fetchData('day_close_summary', null, 'close_date', {
        status: 'closed'
      });

      if (dayCloseSummary && dayCloseSummary.length > 0) {
        // Get the latest closed date
        const sortedDates = dayCloseSummary.sort((a, b) => new Date(b.close_date) - new Date(a.close_date));
        const latestDate = sortedDates[0].close_date;
        
        // Add +1 day to the latest closed date
        const nextDate = new Date(latestDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDateString = nextDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        
        setLatestClosedDate(nextDateString);
        console.log('Latest closed date:', latestDate, '-> Next available date:', nextDateString);
      } else {
        setLatestClosedDate('');
        console.log('No closed dates found');
      }
    } catch (error) {
      console.error('Error fetching latest closed date:', error);
      setLatestClosedDate('');
    }
  }, []);

  // Clear localStorage on logout
  const clearUserData = () => {
    localStorage.removeItem('amusementParkCart');
    localStorage.removeItem('amusementParkTickets');
    setCart([]);
    // Close customer display if open
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      if (customerDisplayWindow.intervalRef) {
        clearInterval(customerDisplayWindow.intervalRef);
      }
      customerDisplayWindow.close();
      setCustomerDisplayWindow(null);
    }
  };

  // Navigation functions
  const handleLogout = () => {
    clearUserData();
    logout();
    navigate('/login');
  };

  // Auto-logout after 30 minutes of inactivity
  // useEffect(() => {
  //   const INACTIVITY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
    
  //   const updateActivity = () => {
  //     setLastActivity(Date.now());
  //   };

  //   const checkInactivity = () => {
  //     const now = Date.now();
  //     if (now - lastActivity > INACTIVITY_TIME) {
  //       // Clear localStorage and logout
  //       localStorage.removeItem('amusementParkCart');
  //       localStorage.removeItem('amusementParkTickets');
  //       // Close customer display if open
  //       if (customerDisplayWindow && !customerDisplayWindow.closed) {
  //         if (customerDisplayWindow.intervalRef) {
  //           clearInterval(customerDisplayWindow.intervalRef);
  //         }
  //         customerDisplayWindow.close();
  //         setCustomerDisplayWindow(null);
  //       }
  //       logout();
  //       navigate('/login');
  //     }
  //   };

  //   // Activity event listeners
  //   const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
  //   activityEvents.forEach(event => {
  //     document.addEventListener(event, updateActivity, true);
  //   });

  //   // Check inactivity every minute
  //   const inactivityTimer = setInterval(checkInactivity, 60000);

  //   return () => {
  //     activityEvents.forEach(event => {
  //       document.removeEventListener(event, updateActivity, true);
  //     });
  //     clearInterval(inactivityTimer);
  //   };
  // }, [lastActivity, navigate, customerDisplayWindow]);

  const navigateToCashierSummary = () => {
    navigate('/pos/cashier-summary');
  };

  // Check counter status function
  const checkCounterStatus = useCallback(async () => {
    try {
      const cashierId = localStorage.getItem('uname') || sessionStorage.getItem('uname') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id');
      
      // Use billing date instead of system date
      if (isLoadingBillingDate || !billingDate) {
        console.log('Billing date not ready, skipping counter status check');
        return;
      }

      console.log('Checking counter status for billing date:', billingDate);

      // Check if counter is already closed for the current billing date
      const existingSummary = await fetchData('cashier_summaries', null, 'id', {
        cashier_id: cashierId,
        setup_date: billingDate,
        status: 'closed'
      });

      if (existingSummary && existingSummary.length > 0) {
        console.log('Counter is closed for billing date:', billingDate);
        
        // Simply show counter closed message and redirect
        setCounterClosed(true);
        const closedTime = new Date(existingSummary[0].session_end_time).toLocaleTimeString();
        setCounterStatusMessage(`Counter was closed at ${closedTime}. Please contact administrator for day close process. Redirecting to summary page...`);
        setShowCounterClosedModal(true);
        setRedirectCountdown(5);
          
        // Start countdown and redirect after 5 seconds
        const countdownInterval = setInterval(() => {
          setRedirectCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              navigate('/pos/cashier-summary');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(countdownInterval);
      } else {
        // Counter is not closed, allow billing
        console.log('Counter is open for billing date:', billingDate);
        setCounterClosed(false);
        setCounterStatusMessage('');
        setShowCounterClosedModal(false);
      }
    } catch (error) {
      console.error('Error checking counter status:', error);
      // If there's an error, allow billing to continue (fail-open approach)
      setCounterClosed(false);
    }
  }, [navigate, billingDate, isLoadingBillingDate]);

  // Fetch latest closed date on component mount
  useEffect(() => {
    fetchLatestClosedDate();
  }, [fetchLatestClosedDate]);

  // Check counter status on component mount
  useEffect(() => {
    if (isCashier) {
      checkCounterStatus();
    }
  }, [isCashier, checkCounterStatus]);

  // Customer Display Functions
  const closeCustomerDisplay = () => {
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Clean up interval if it exists
      if (customerDisplayWindow.intervalRef) {
        clearInterval(customerDisplayWindow.intervalRef);
      }
      customerDisplayWindow.close();
      setCustomerDisplayWindow(null);
    }
  };

  const toggleCustomerDisplay = () => {
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      closeCustomerDisplay();
    } else {
      openCustomerDisplayOnSecondScreen();
    }
  };

  const navigateToSalesReport = () => {
    navigate('/reports/saleledger');
  };

  // Function to detect and open customer display on second screen
  const openCustomerDisplayOnSecondScreen = () => {
    try {
      // Get all available screens
      if (window.screen && window.screen.getAllDisplays) {
        // Modern API for multiple displays
        window.screen.getAllDisplays().then((displays) => {
          const secondDisplay = displays.find((display, index) => index > 0) || displays[0];
          openCustomerDisplayWindow(secondDisplay);
        }).catch(() => {
          // Fallback to traditional method
          openCustomerDisplayWithFallback();
        });
      } else {
        // Fallback for browsers without multi-display API
        openCustomerDisplayWithFallback();
      }
    } catch (error) {
      console.log('Multi-display API not supported, using fallback');
      openCustomerDisplayWithFallback();
    }
  };

  const openCustomerDisplayWithFallback = () => {
    // Detect screen dimensions
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    
    // Calculate position for second screen (assuming dual monitor setup)
    const windowWidth = Math.min(800, screenWidth * 0.8);
    const windowHeight = Math.min(600, screenHeight * 0.8);
    
    // Try to position on right side (second monitor)
    const leftPosition = screenWidth > 1920 ? screenWidth - windowWidth : screenWidth * 0.1;
    const topPosition = screenHeight * 0.1;
    
    openCustomerDisplayWindow({
      left: leftPosition,
      top: topPosition,
      width: windowWidth,
      height: windowHeight
    });
  };

  const openCustomerDisplayWindow = (displayConfig) => {
    // Close existing window if open
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      customerDisplayWindow.close();
    }

    const windowFeatures = [
      `width=${displayConfig.width || 800}`,
      `height=${displayConfig.height || 600}`,
      `left=${displayConfig.left || 100}`,
      `top=${displayConfig.top || 100}`,
      'resizable=yes',
      'scrollbars=no',
      'toolbar=no',
      'menubar=no',
      'location=no',
      'status=no'
    ].join(',');

    const newWindow = window.open('', 'CustomerDisplay', windowFeatures);
    
    if (newWindow) {
      setCustomerDisplayWindow(newWindow);
      updateCustomerDisplayContent(newWindow);
      
      // Handle window close event properly
      newWindow.addEventListener('unload', () => {
        setCustomerDisplayWindow(null);
      });
      
      // Check window status periodically and handle updates
      const interval = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(interval);
          setCustomerDisplayWindow(null);
        } else {
          // Get the most current cart data from localStorage to ensure sync
          try {
            const savedCart = localStorage.getItem('amusementParkCart');
            const currentCartData = savedCart ? JSON.parse(savedCart) : [];
            updateCustomerDisplayContent(newWindow, currentCartData);
          } catch (error) {
            console.log('Customer display update error:', error);
            // If update fails, window might be closed or localStorage is corrupted
            if (newWindow.closed) {
              clearInterval(interval);
              setCustomerDisplayWindow(null);
            } else {
              // Fallback to React state if localStorage fails
              updateCustomerDisplayContent(newWindow);
            }
          }
        }
      }, 10000); // Reduced frequency to 10 seconds since we have real-time updates
      
      // Store interval reference to clean up later
      newWindow.intervalRef = interval;
    } else {
      alert('Please allow pop-ups for customer display functionality');
    }
  };

  // Calculate total amount
  const getTotalAmount = useCallback(() => {
    const total = cart.reduce((sum, item) => {
      const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
      return sum + itemTotal;
    }, 0);
    return total.toFixed(2);
  }, [cart]);

  const updateCustomerDisplayContent = useCallback((window, cartData = null) => {
    if (!window || window.closed) return;

    // Use provided cartData or fallback to current cart state
    const currentCart = cartData !== null ? cartData : cart;

    // Check if document is already initialized
    if (window.document && window.document.querySelector('.cart-section')) {
      // Document exists, just update cart content
      try {
        const cartSubtitle = window.document.querySelector('.cart-subtitle');
        const cartContent = window.document.querySelector('.cart-content');
        const cartSection = window.document.querySelector('.cart-section');
        
        if (cartSubtitle) {
          cartSubtitle.innerHTML = `Items: ${currentCart.length}`;
        }
        
        if (cartContent) {
          cartContent.innerHTML = currentCart.length === 0 ? `
            <div class="cart-empty">
              <div class="cart-empty-icon">🛍️</div>
              <div>Your cart is empty</div>
              <div style="font-size: 0.8em; margin-top: 10px; opacity: 0.7;">
                Add some tickets to get started!
              </div>
            </div>
          ` : `
            <div class="cart-items">
              ${currentCart.map(item => `
                <div class="cart-item">
                  <div class="item-info">
                    <div class="item-name">🎟️ ${item.item_name}</div>
                    <div class="item-quantity">Quantity: ${item.quantity}</div>
                  </div>
                  <div class="item-price">
                    ฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}
                  </div>
                </div>
              `).join('')}
            </div>
          `;
        }
        
        // Update or remove total section
        let totalSection = window.document.querySelector('.cart-total');
        if (currentCart.length > 0) {
          if (!totalSection) {
            // Create total section if it doesn't exist
            totalSection = window.document.createElement('div');
            totalSection.className = 'cart-total';
            cartSection.appendChild(totalSection);
          }
          totalSection.innerHTML = `
            <div class="total-label">Total Amount</div>
            <div class="total-amount">฿${currentCart.reduce((sum, item) => {
              const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
              return sum + itemTotal;
            }, 0).toFixed(2)}</div>
          `;
        } else if (totalSection) {
          // Remove total section if cart is empty
          totalSection.remove();
        }
        
        return; // Exit early, content updated successfully
      } catch (error) {
        console.log('Error updating cart content, falling back to full reload:', error);
        // If update fails, fall through to full document rewrite
      }
    }

    // Full document initialization (first time or fallback)
    const customerDisplayHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Customer Display - Amusement Park</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #1a1f35 100%);
            color: #e2e8f0;
            height: 100vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .header {
            background: rgba(15, 23, 42, 0.85);
            padding: 15px 20px;
            text-align: center;
            border-bottom: 2px solid rgba(251, 191, 36, 0.3);
            flex-shrink: 0;
            backdrop-filter: blur(10px);
          }

          .header h1 {
            font-size: 2em;
            margin-bottom: 5px;
            text-shadow: 0 0 10px rgba(251, 191, 36, 0.3);
            color: #fbbf24;
          }

          .header p {
            font-size: 1em;
            opacity: 0.9;
            color: #cbd5e1;
          }
          
          .main-content {
            flex: 1;
            display: flex;
            height: calc(100vh - 120px);
          }
          
          .ads-section {
            flex: 1;
            background: rgba(30, 41, 59, 0.6);
            display: flex;
            flex-direction: column;
            padding: 20px;
            border-right: 2px solid rgba(251, 191, 36, 0.2);
            position: relative;
            overflow: hidden;
          }
          
          .ads-horizontal-container {
            width: 100%;
            height: 100%;
            display: flex;
            gap: 15px;
            flex-direction: column;
          }
          
          .ad-section-1,
          .ad-section-2,
          .ad-section-3 {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(15, 23, 42, 0.7);
            border-radius: 15px;
            overflow: hidden;
            position: relative;
            border: 1px solid rgba(251, 191, 36, 0.15);
          }

          .ad-section-1 iframe,
          .ad-section-2 img,
          .ad-section-3 iframe {
            width: 100%;
            height: 100%;
            object-fit: contain;
            border: none;
          }
          
          .ad-placeholder {
            text-align: center;
            font-size: 1.2em;
            color: rgba(251, 191, 36, 0.6);
          }
          
          .cart-section {
            flex: 1;
            padding: 20px;
            display: flex;
            flex-direction: column;
            background: rgba(30, 41, 59, 0.6);
            border-left: 2px solid rgba(251, 191, 36, 0.2);
          }

          .cart-header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid rgba(251, 191, 36, 0.2);
          }

          .cart-title {
            font-size: 1.8em;
            font-weight: 700;
            color: #fbbf24;
            text-shadow: 0 0 8px rgba(251, 191, 36, 0.3);
            margin-bottom: 5px;
          }

          .cart-subtitle {
            font-size: 1em;
            opacity: 0.9;
            color: #cbd5e1;
          }
          
          .cart-content {
            flex: 1;
            overflow-y: auto;
            padding-right: 10px;
          }
          
          .cart-content::-webkit-scrollbar {
            width: 8px;
          }
          
          .cart-content::-webkit-scrollbar-track {
            background: rgba(251, 191, 36, 0.08);
            border-radius: 4px;
          }

          .cart-content::-webkit-scrollbar-thumb {
            background: rgba(251, 191, 36, 0.4);
            border-radius: 4px;
          }

          .cart-content::-webkit-scrollbar-thumb:hover {
            background: rgba(251, 191, 36, 0.6);
          }
          
          .cart-empty {
            text-align: center;
            padding: 40px 20px;
            font-size: 1.5em;
            opacity: 0.8;
          }
          
          .cart-empty-icon {
            font-size: 4em;
            margin-bottom: 20px;
          }
          
          .cart-items {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          
          .cart-item {
            background: rgba(251, 191, 36, 0.08);
            padding: 15px;
            border-radius: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(251, 191, 36, 0.15);
            transition: all 0.3s ease;
            font-size: xx-large;
          }

          .cart-item:hover {
            background: rgba(251, 191, 36, 0.15);
            transform: translateX(5px);
            box-shadow: 0 3px 10px rgba(251, 191, 36, 0.2);
          }

          .item-info {
            display: flex;
            flex-direction: column;
            gap: 5px;
          }

          .item-name {
            font-size: 1.1em;
            font-weight: 600;
            color: #e2e8f0;
          }

          .item-quantity {
            font-size: 0.9em;
            opacity: 0.9;
            color: #4ade80;
          }

          .item-price {
            font-size: 1.3em;
            font-weight: 700;
            color: #fbbf24;
            text-shadow: 0 0 6px rgba(251, 191, 36, 0.3);
          }

          .cart-total {
            margin-top: 20px;
            padding: 20px;
            background: rgba(15, 23, 42, 0.7);
            border-radius: 15px;
            border: 2px solid rgba(251, 191, 36, 0.3);
            text-align: center;
            font-size: xxx-large;
          }

          .total-label {
            font-size: 1.2em;
            margin-bottom: 5px;
            opacity: 0.9;
            color: #e2e8f0;
          }

          .total-amount {
            font-size: 2.5em;
            font-weight: 700;
            color: #fbbf24;
            text-shadow: 0 0 10px rgba(251, 191, 36, 0.4);
          }
          
          .footer {
            background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.9) 100%);
            padding: 12px 20px;
            border-top: 2px solid rgba(251, 191, 36, 0.3);
            flex-shrink: 0;
            backdrop-filter: blur(10px);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
          }

          .footer-logo-section {
            display: flex;
            align-items: center;
            flex-shrink: 0;
          }

          .footer-logo-img {
            width: 50px;
            height: 50px;
            object-fit: contain;
            filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.3));
          }

          .footer-text-section {
            flex: 1;
            text-align: center;
          }

          .footer p {
            font-size: 1em;
            opacity: 0.95;
            color: #fbbf24;
            font-weight: 600;
            text-shadow: 0 0 6px rgba(251, 191, 36, 0.2);
            margin: 0;
            line-height: 1.3;
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .cart-item {
            animation: slideIn 0.3s ease;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }
          
          .cart-total {
            animation: pulse 2s ease-in-out infinite;
          }
          
          @keyframes rotate-ads {
            0% { opacity: 1; }
            45% { opacity: 1; }
            50% { opacity: 0; }
            95% { opacity: 0; }
            100% { opacity: 1; }
          }
          
          .ad-container {
            animation: rotate-ads 10s infinite;
          }
          
          @media (max-width: 768px) {
            .main-content {
              flex-direction: column;
            }
            
            .ads-section {
              flex: 0.4;
              border-right: none;
              border-bottom: 2px solid rgba(255, 255, 255, 0.3);
            }
            
            .cart-section {
              flex: 0.6;
            }
            
            .header h1 { font-size: 1.5em; }
            .cart-title { font-size: 1.4em; }
            .total-amount { font-size: 2em; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🎡 The View Pattaya</h1>
          <p>Amusement Park Management System - Welcome to the Ultimate Fun Experience!</p>
        </div>
        
        <div class="main-content">
          <!-- Ads Section (Left) - 3 Horizontal Sections -->
          <div class="ads-section">
            <div class="ads-horizontal-container" id="adsContainer">
              <div class="ad-section-1" id="adSection1">
                <div class="ad-placeholder">🎥 Loading Video...</div>
              </div>
              <div class="ad-section-2" id="adSection2">
                <div class="ad-placeholder">🏢 Loading Logo...</div>
              </div>
              <div class="ad-section-3" id="adSection3">
                <div class="ad-placeholder">🎥 Loading Video...</div>
              </div>
            </div>
          </div>
          
          <!-- Cart Section (Right) -->
          <div class="cart-section">
            <div class="cart-header">
              <div class="cart-title">🛒 Your Order</div>
              <div class="cart-subtitle">Items: ${currentCart.length}</div>
            </div>
            
            <div class="cart-content">
              ${currentCart.length === 0 ? `
                <div class="cart-empty">
                  <div class="cart-empty-icon">🛍️</div>
                  <div>Your cart is empty</div>
                  <div style="font-size: 0.8em; margin-top: 10px; opacity: 0.7;">
                    Add some tickets to get started!
                  </div>
                </div>
              ` : `
                <div class="cart-items">
                  ${currentCart.map(item => `
                    <div class="cart-item">
                      <div class="item-info">
                        <div class="item-name">🎟️ ${item.item_name}</div>
                        <div class="item-quantity">Quantity: ${item.quantity}</div>
                      </div>
                      <div class="item-price">
                        ฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}
                      </div>
                    </div>
                  `).join('')}
                </div>
              `}
            </div>
            
            ${currentCart.length > 0 ? `
              <div class="cart-total">
                <div class="total-label">Total Amount</div>
                <div class="total-amount">฿${currentCart.reduce((sum, item) => {
                  const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
                  return sum + itemTotal;
                }, 0).toFixed(2)}</div>
              </div>
            ` : ''}
          </div>
        </div>
        
        <div class="footer">
          <div class="footer-logo-section">
            <img src="/assets/cloudnetlogo.png" alt="Cloudnet Softwares" class="footer-logo-img" onerror="this.style.display='none'" />
          </div>
          <div class="footer-text-section">
            <p>🎠 Software Developed by Cloudnet Softwares<br/>📞 +66-948712350 (Eng) | +66-952477020 (Thai) ✨</p>
          </div>
        </div>
        
        <!-- QR Code Overlay Container -->
        <div id="qrCodeContainer" style="
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          z-index: 10000;
          justify-content: center;
          align-items: center;
        "></div>
        
        <script>
          // Fetch ads from API and display
          async function loadAdsFromAPI() {
            try {
              // Get auth token from storage
              const token = localStorage.getItem('token') || sessionStorage.getItem('token');
              const authToken = token ? token.replace(/^"(.*)"$/, '$1') : '';
              const bearerToken = authToken.startsWith('Bearer ') ? authToken : 'Bearer ' + authToken;
              
              // Use full URL since this is in a separate window context
              const apiUrl = 'https://wheelapi.livecloudnet.com/api/cashier/ads-sections';
              
              const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                  'Authorization': bearerToken,
                  'Content-Type': 'application/json'
                }
              });
              
              
              
              if (response.ok) {
                const adsData = await response.json();
                console.log('Ads data loaded:', adsData);
                
                // Filter active ads and render
                const activeAds = adsData.filter(ad => ad.is_active);
                console.log('Active ads:', activeAds.length);
                
                if (activeAds.length > 0) {
                  activeAds.forEach(ad => {
                    renderAdSection(ad);
                  });
                } else {
                  console.warn('No active ads found');
                  showFallbackAds();
                }
              } else {
                const errorText = await response.text();
                console.error('Failed to load ads:', response.status, errorText);
                showFallbackAds();
              }
            } catch (error) {
              console.error('Error loading ads:', error.message, error);
              showFallbackAds();
            }
          }
          
          // Extract YouTube video ID from URL
          function extractYouTubeVideoId(url) {
            if (!url) return null;
            const regExp = /^.*(youtu.be\\/|v\\/|u\\/\\w\\/|embed\\/|watch\\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
          }
          
          // Render individual ad section
          function renderAdSection(ad) {
            const sectionId = 'adSection' + ad.section_number;
            const sectionElement = document.getElementById(sectionId);
            
            if (!sectionElement) return;
            
            if (ad.section_type === 'video' && ad.youtube_url) {
              const videoId = extractYouTubeVideoId(ad.youtube_url);
              if (videoId) {
                sectionElement.innerHTML = \`
                  <iframe
                    src="https://www.youtube.com/embed/\${videoId}?autoplay=1&mute=1&loop=1&playlist=\${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1"
                    title="\${ad.title || 'Video Advertisement'}"
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                    style="width: 100%; height: 100%; border: none;">
                  </iframe>
                \`;
              }
            } else if (ad.section_type === 'logo' && ad.logo_url) {
              sectionElement.innerHTML = \`
                <img 
                  src="\${ad.logo_url}"
                  alt="\${ad.title || 'Company Logo'}"
                  style="max-width: 90%; max-height: 90%; object-fit: contain;">
              \`;
            } else if (ad.section_type === 'logo') {
              // Fallback to local logo if no logo_url
              sectionElement.innerHTML = \`
                <img 
                  src="/logo.png"
                  alt="Company Logo"
                  style="max-width: 90%; max-height: 90%; object-fit: contain;">
              \`;
            }
          }
          
          // Fallback ads if API fails
          function showFallbackAds() {
            document.getElementById('adSection1').innerHTML = \`
              <div class="ad-placeholder">
                <div style="font-size: 2em; margin-bottom: 10px;">🎡</div>
                <div>Welcome to The View Pattaya!</div>
              </div>
            \`;
            document.getElementById('adSection2').innerHTML = \`
              <img 
                src="/logo.png"
                alt="The View Pattaya Logo"
                style="max-width: 90%; max-height: 90%; object-fit: contain;">
            \`;
            document.getElementById('adSection3').innerHTML = \`
              <div class="ad-placeholder">
                <div style="font-size: 2em; margin-bottom: 10px;">🎢</div>
                <div>Fun for Everyone!</div>
              </div>
            \`;
          }
          
          // Initialize on page load
          document.addEventListener('DOMContentLoaded', function() {
            loadAdsFromAPI();
            
            // Refresh ads every 5 minutes
            setInterval(loadAdsFromAPI, 5 * 60 * 1000);
          });
          
          // Enhanced animations for new items
          const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
              if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(function(node) {
                  if (node.nodeType === 1 && node.classList.contains('cart-item')) {
                    node.style.transform = 'translateX(20px)';
                    node.style.opacity = '0';
                    setTimeout(() => {
                      node.style.transition = 'all 0.3s ease';
                      node.style.transform = 'translateX(0)';
                      node.style.opacity = '1';
                    }, 50);
                  }
                });
              }
            });
          });
          
          const cartContent = document.querySelector('.cart-items');
          if (cartContent) {
            observer.observe(cartContent, { childList: true });
          }
        </script>
      </body>
      </html>
    `;

    window.document.open();
    window.document.write(customerDisplayHtml);
    window.document.close();
  }, [cart]);

  useEffect(() => {
    // Fetch categories and all tickets
    const fetchCategoriesAndTickets = async () => {
      try {
        setLoading(true);
        console.log('Fetching categories...');
        const categoriesData = await fetchData("categories", () => {}, "id", {});
        console.log('Categories data:', categoriesData);
        
        // Ensure categoriesData is an array and handle null/undefined
        const categories = Array.isArray(categoriesData) ? categoriesData : [];
        setCategories(categories);
        
        if (categories.length === 0) {
          console.log('No categories found');
          setTicketPricing({});
          return;
        }
        
        // Fetch tickets for all categories
        const ticketsByCategory = {};
        for (const category of categories) {
          try {
            const ticketsResponse = await fetchData("ticket_pricing", () => {}, "id", { category_id: category.id });
            // console.log(`Tickets for category ${category.name}:`, ticketsResponse);
            ticketsByCategory[category.id] = Array.isArray(ticketsResponse) ? ticketsResponse : [];
          } catch (error) {
            console.error(`Error fetching tickets for category ${category.name}:`, error);
            ticketsByCategory[category.id] = [];
          }
        }
        setTicketPricing(ticketsByCategory);
        // console.log('All ticket pricing:', ticketsByCategory);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setTicketPricing({});
      } finally {
        setLoading(false);
      }
    };
    
    // Load cart from localStorage
    const savedCart = localStorage.getItem('amusementParkCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        // Ensure all cart items have proper numeric values
        const validatedCart = parsedCart.map(item => ({
          ...item,
          price: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
          quantity: typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 1,
          total: typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0
        }));
        setCart(validatedCart);
      } catch (error) {
        console.error("Error parsing saved cart:", error);
        localStorage.removeItem('amusementParkCart');
      }
    }
    
    fetchCategoriesAndTickets();
  }, []);

  // Sync cart changes with customer display in real-time
  // Note: Direct updates are now handled in addToCart, removeFromCart, updateQuantity, and clearCart
  // This effect serves as a backup for other cart state changes
  useEffect(() => {
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Only update if cart actually changed and it's not handled by direct functions
      updateCustomerDisplayContent(customerDisplayWindow);
    }
  }, [cart, customerDisplayWindow, updateCustomerDisplayContent]);

  // Cleanup effect for customer display window
  useEffect(() => {
    return () => {
      if (customerDisplayWindow && !customerDisplayWindow.closed) {
        if (customerDisplayWindow.intervalRef) {
          clearInterval(customerDisplayWindow.intervalRef);
        }
        customerDisplayWindow.close();
      }
    };
  }, [customerDisplayWindow]);

  // Monitor customer display window state
  useEffect(() => {
    if (customerDisplayWindow) {
      const checkWindowState = () => {
        if (customerDisplayWindow.closed) {
          setCustomerDisplayWindow(null);
        }
      };
      
      // Check immediately and then every 2 seconds
      const stateMonitor = setInterval(checkWindowState, 2000);
      
      return () => {
        clearInterval(stateMonitor);
      };
    }
  }, [customerDisplayWindow]);

  const addToCart = (ticket) => {
    const existingItem = cart.find(item => item.id === ticket.id);
    const ticketPrice = typeof ticket.price === 'number' ? ticket.price : parseFloat(ticket.price) || 0;
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item => 
        item.id === ticket.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * ticketPrice }
          : item
      );
    } else {
      newCart = [...cart, { ...ticket, quantity: 1, total: ticketPrice }];
    }
    setCart(newCart);
    // Save to localStorage
    localStorage.setItem('amusementParkCart', JSON.stringify(newCart));
    
    // Real-time update customer display
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Pass the updated cart directly to ensure immediate sync
      updateCustomerDisplayContent(customerDisplayWindow, newCart);
    }
  };

  const removeFromCart = (ticketId) => {
    const newCart = cart.filter(item => item.id !== ticketId);
    setCart(newCart);
    // Save to localStorage
    localStorage.setItem('amusementParkCart', JSON.stringify(newCart));
    
    // Real-time update customer display
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Pass the updated cart directly to ensure immediate sync
      updateCustomerDisplayContent(customerDisplayWindow, newCart);
    }
  };

  const updateQuantity = (ticketId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(ticketId);
      return;
    }
    const newCart = cart.map(item => {
      if (item.id === ticketId) {
        const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
        return { ...item, quantity: newQuantity, total: newQuantity * itemPrice };
      }
      return item;
    });
    setCart(newCart);
    // Save to localStorage
    localStorage.setItem('amusementParkCart', JSON.stringify(newCart));
    
    // Real-time update customer display
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Pass the updated cart directly to ensure immediate sync
      updateCustomerDisplayContent(customerDisplayWindow, newCart);
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('amusementParkCart');
    
    // Immediately update customer display with empty cart
    if (customerDisplayWindow && !customerDisplayWindow.closed) {
      // Pass empty array directly to ensure customer display shows empty cart
      updateCustomerDisplayContent(customerDisplayWindow, []);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  // Handle direct payment without modal
  const handleDirectPayment = async (paymentMethod) => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    // Check if counter is closed
    if (counterClosed) {
      toast.error(counterStatusMessage || 'Counter is closed. Cannot process new bills.');
      return;
    }

    // Check if billing date is available
    if (isLoadingBillingDate) {
      toast.error('Billing date is loading. Please wait...');
      return;
    }

    if (!billingDate) {
      toast.error('Billing date not available. Cannot process payment.');
      return;
    }

    try {
      // Prepare bill data
      const billData = {
        customer_name: 'Walk-in Customer',
        customer_phone: null,
        payment_method: paymentMethod,
        cart_items: cart.map(item => ({
          id: item.id,
          item_name: item.item_name,
          category_id: item.category_id || null,
          category_name: item.category_name || null,
          price: parseFloat(item.price) || 0,
          quantity: parseInt(item.quantity) || 1,
          total: parseFloat(item.total) || 0
        }))
      };

      console.log(`Processing ${paymentMethod} payment...`);
      console.log('Using billing date for ticket:', billingDate);

      if (paymentMethod === 'cash') {
        // For cash payment, just prepare payment data and show modal
        // Database save will happen when user selects print option
        console.log('Cash payment selected - showing ticket options...');
        
        const paymentData = {
          customer_name: billData.customer_name,
          customer_phone: billData.customer_phone,
          total_amount: getTotalAmount(),
          payment_method: paymentMethod,
          notes: null
        };

        // Show ticket printing options modal (payment will be saved when user selects printing option)
        showTicketPrintingOptions(paymentData);
      } 
      else if (paymentMethod === 'qr') {
        // For QR payment, generate SCB QR code and wait for confirmation
       // console.log('Processing SCB QR payment...');
        toast.info('Generating SCB QR Code... Please wait');
        
        // Prepare order details for SCB (moved outside try block for broader scope)
        const orderDetails = {
          orderId: `ORDER-${Date.now()}`,
          customerName: billData.customer_name,
          items: billData.cart_items,
          cashierId: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Unknown',
          timestamp: new Date().toISOString()
        };
        
        try {

          // Generate SCB QR payment
          console.log('Calling SCB API with data:', {
            paymentAmount: parseFloat(getTotalAmount()),
            orderDetails: orderDetails
          });
          
          const scbResponse = await axios.post(
            '/scb/generate-qr-payment',
            {
              paymentAmount: parseFloat(getTotalAmount()),
              orderDetails: orderDetails
            },
            getHeaders()
          );

          console.log('SCB Response:', scbResponse.data);
          console.log('SCB Response full structure:', JSON.stringify(scbResponse.data, null, 2));

          if (scbResponse.data && scbResponse.data.success) {
            // Extract QR data from the new response format
            const qrResponseData = {
              qrString: scbResponse.data.deeplinkUrl || scbResponse.data.qrData?.qrRawData,
              qrRawData: scbResponse.data.deeplinkUrl || scbResponse.data.qrData?.qrRawData,
              deeplinkUrl: scbResponse.data.deeplinkUrl,
              transactionId: scbResponse.data.qrData?.transactionId || orderDetails.orderId,
              amount: scbResponse.data.amount || getTotalAmount(),
              ref1: scbResponse.data.qrData?.ref1 || orderDetails.orderId,
              ref2: scbResponse.data.qrData?.ref2,
              ref3: scbResponse.data.qrData?.ref3,
              expiryDateTime: scbResponse.data.qrData?.expiryDateTime,
              isLegacy: false
            };
            
            console.log('Formatted QR response data:', qrResponseData);
            
            // Show QR code modal to customer
            showSCBQRModal(qrResponseData, billData, orderDetails);
          } else {
            throw new Error(scbResponse.data?.error || scbResponse.data?.message || 'No QR code received from SCB API');
          }
        } catch (error) {
          console.error('SCB QR generation error:', error);
          
          // Fallback to legacy QR code generation
          console.log('Falling back to legacy QR code generation...');
          toast.warn('SCB QR service unavailable. Using alternative QR code...');
          
          try {
            // Generate legacy Thai QR code as fallback
            const qrData = generateThaiQRCode(getTotalAmount(), `TEMP-${Date.now()}`);
            
            // Show QR modal with legacy QR code
            showSCBQRModal({
              qrString: qrData.qrString,
              transactionId: qrData.billNumber,
              amount: getTotalAmount(),
              isLegacy: true
            }, billData, orderDetails);
            
          } catch (fallbackError) {
            console.error('Fallback QR generation also failed:', fallbackError);
            toast.error('Failed to generate QR code. Please try cash or card payment.');
          }
        }
      }
       else {
        // For other payment methods, prepare payment data but DON'T save yet
        // Show modal first, then save only if user confirms
        const paymentData = {
          customer_name: billData.customer_name,
          customer_phone: billData.customer_phone,
          total_amount: getTotalAmount(),
          payment_method: paymentMethod,
          notes: null
        };

        // Show ticket printing options modal (payment will be saved only if user selects a printing option)
        showTicketPrintingOptions(paymentData);
      }
    } catch (error) {
      console.error(`${paymentMethod} payment error:`, error);
      toast.error(
        error.response?.data?.message || 
        error.message || 
        `Failed to process ${paymentMethod} payment. Please try again.`
      );
    }
  };

  // Thai QR Code generation function (Thai QR Payment Standard compliant)
  const generateThaiQRCode = (amount, billNumber, promptPayId = null) => {
    // Use provided PromptPay ID or default (replace with your actual PromptPay ID)
    // Format: Mobile number (13 digits) or Tax ID (13 digits) or e-Wallet ID
    const promptPayID = promptPayId || "0891234567890"; // Replace with actual PromptPay ID
    
    // Helper function to calculate CRC16 (CCITT-FALSE algorithm)
    const calculateCRC16 = (data) => {
      let crc = 0xFFFF;
      for (let i = 0; i < data.length; i++) {
        crc ^= data.charCodeAt(i) << 8;
        for (let j = 0; j < 8; j++) {
          if (crc & 0x8000) {
            crc = (crc << 1) ^ 0x1021;
          } else {
            crc = crc << 1;
          }
          crc &= 0xFFFF;
        }
      }
      return crc.toString(16).toUpperCase().padStart(4, '0');
    };

    // Build Thai QR Payment Standard format (EMV QR Code Specification)
    let qrString = '';
    
    // Payload Format Indicator (Tag 00) - Always "01"
    qrString += '000201';
    
    // Point of Initiation Method (Tag 01) - "12" for dynamic QR
    qrString += '010212';
    
    // Merchant Account Information (Tag 29 for PromptPay Thailand)
    const promptPayTag = '29';
    // PromptPay Application ID: A000000677010111
    const applicationId = 'A000000677010111';
    const promptPayData = `00${applicationId.length.toString().padStart(2, '0')}${applicationId}01${promptPayID.length.toString().padStart(2, '0')}${promptPayID}`;
    const promptPayLength = promptPayData.length.toString().padStart(2, '0');
    qrString += `${promptPayTag}${promptPayLength}${promptPayData}`;
    
    // Transaction Currency (Tag 53) - Thai Baht (ISO 4217 code: 764)
    qrString += '5303764';
    
    // Transaction Amount (Tag 54) - Only include if amount > 0
    const amountStr = parseFloat(amount).toFixed(2);
    if (parseFloat(amount) > 0) {
      const amountLength = amountStr.length.toString().padStart(2, '0');
      qrString += `54${amountLength}${amountStr}`;
    }
    
    // Country Code (Tag 58) - Thailand (ISO 3166-1 alpha-2)
    qrString += '5802TH';
    
    // Merchant Name (Tag 59) - Max 25 characters
    const merchantName = 'The View Pattaya';
    const merchantNameLength = merchantName.length.toString().padStart(2, '0');
    qrString += `59${merchantNameLength}${merchantName}`;
    
    // Merchant City (Tag 60) - Max 15 characters
    const merchantCity = 'Pattaya';
    const merchantCityLength = merchantCity.length.toString().padStart(2, '0');
    qrString += `60${merchantCityLength}${merchantCity}`;
    
    // Additional Data Field Template (Tag 62) - Bill Reference
    if (billNumber) {
      const billRef = billNumber.toString().substring(0, 25); // Max 25 characters
      const billRefLength = billRef.length.toString().padStart(2, '0');
      const additionalData = `01${billRefLength}${billRef}`;
      const additionalDataLength = additionalData.length.toString().padStart(2, '0');
      qrString += `62${additionalDataLength}${additionalData}`;
    }
    
    // CRC (Tag 63) - Calculate and append (always last)
    const qrWithoutCRC = qrString + '6304';
    const crc = calculateCRC16(qrWithoutCRC);
    qrString += `6304${crc}`;
    
    console.log('🇹🇭 Generated Thai QR Code:', {
      promptPayID: promptPayID,
      amount: amountStr,
      billNumber: billNumber,
      qrLength: qrString.length,
      qrString: qrString
    });
    
    const qrData = {
      paymentMethod: "PromptPay",
      merchantID: promptPayID,
      amount: amount,
      billNumber: billNumber,
      qrString: qrString,
      standard: "Thai QR Payment Standard",
      version: "EMV QR Code Specification",
      expiryTime: new Date(Date.now() + 15 * 60 * 1000) // 15 minutes from now
    };

    return qrData;
  };

  // Generate SCB QR Code using Thai QR Standard
  const generateSCBQRCode = async (amount, ref1, ref2 = "POS1", ref3 = "CHEFMATE") => {
    try {
      console.log('🇹🇭 Generating Thai QR Standard code...');
      
      // Use Thai QR Code Standard format instead of SCB proprietary format
      const thaiQrData = generateThaiQRCode(amount, ref1);
      
      // Return in format expected by the modal
      return {
        qrRawData: thaiQrData.qrString,
        qrString: thaiQrData.qrString,
        ref1: ref1,
        amount: amount,
        paymentMethod: thaiQrData.paymentMethod,
        merchantID: thaiQrData.merchantID,
        billNumber: thaiQrData.billNumber,
        expiryTime: thaiQrData.expiryTime,
        isThaiStandard: true,
        transactionId: ref1
      };
      
    } catch (error) {
      console.error('Thai QR Code generation error:', error);
      
      // If something fails, try the SCB API as fallback
      try {
        console.log('Falling back to SCB API...');
        const response = await axios.post('/scb/qrcode', {
          amount: parseFloat(amount),
          ref1: ref1,
          ref2: ref2,
          ref3: ref3
        }, getHeaders());
        
        console.log('SCB QR API response:', response.data);
        
        // Handle the new SCB response format
        if (response.data && response.data.success && response.data.data) {
          return {
            qrRawData: response.data.data.qrRawData,
            qrString: response.data.data.qrRawData,
            ref1: response.data.data.ref1,
            ref2: response.data.data.ref2,
            ref3: response.data.data.ref3,
            amount: response.data.data.amount,
            transactionId: response.data.data.transactionId,
            expiryDateTime: response.data.data.expiryDateTime,
            isLegacy: false
          };
        }
        
        return response.data;
      } catch (apiError) {
        console.error('SCB API also failed:', apiError);
        
        // If SCB API fails, try the legacy endpoint
        if (apiError.response?.status === 401 || apiError.response?.status === 404) {
          console.log('Trying legacy SCB endpoint...');
          try {
            const legacyResponse = await axios.post('/scb/generate-qr-payment', {
              paymentAmount: parseFloat(amount),
              orderDetails: {
                orderId: ref1,
                customerId: ref2,
                transactionId: ref3
              }
            }, getHeaders());
            
            return legacyResponse.data;
          } catch (legacyError) {
            console.error('Legacy SCB endpoint also failed:', legacyError);
            throw legacyError;
          }
        }
        
        throw apiError;
      }
    }
  };

  // Check SCB payment status
  const checkSCBPaymentStatus = async (ref1) => {
    try {
      // Try the new status endpoint first
      const response = await axios.get(`/scb/status/${ref1}`, getHeaders());
      return response.data;
    } catch (error) {
      console.error('SCB payment status check error:', error);
      
      // If new endpoint fails, try the legacy transaction endpoint
      if (error.response?.status === 404) {
        console.log('Trying legacy transaction endpoint...');
        try {
          const legacyResponse = await axios.get(`/scb/transaction/${ref1}`, getHeaders());
          return legacyResponse.data;
        } catch (legacyError) {
          console.error('Legacy transaction endpoint failed:', legacyError);
          throw legacyError;
        }
      }
      
      throw error;
    }
  };

  // Simulate SCB payment (for testing)
  const simulateSCBPayment = async (amount, ref1, ref2 = "POS1", ref3 = "CHEFMATE") => {
    try {
      await axios.post('/scb/simulate', {
        amount: parseFloat(amount),
        ref1: ref1,
        ref2: ref2,
        ref3: ref3
      }, getHeaders());
      
      return true;
    } catch (error) {
      console.error('SCB payment simulation error:', error);
      throw error;
    }
  };

  // Debug function to test SCB endpoints
  const debugSCBEndpoints = async () => {
    try {
      console.log('🔍 Testing SCB endpoints...');
      
      // Test health endpoint
      try {
        const healthResponse = await axios.get('/scb/health');
        console.log('✅ Health endpoint working:', healthResponse.data);
      } catch (healthError) {
        console.log('❌ Health endpoint failed:', healthError.response?.status);
      }
      
      // Test debug endpoint
      try {
        const debugResponse = await axios.post('/scb/debug-qr-request', {
          paymentAmount: 100,
          orderDetails: { orderId: 'TEST123' }
        });
        console.log('✅ Debug endpoint working:', debugResponse.data);
      } catch (debugError) {
        console.log('❌ Debug endpoint failed:', debugError.response?.status);
      }
      
    } catch (error) {
      console.error('Debug failed:', error);
    }
  };

  // Show SCB QR Code modal for payment
  // Helper function to show QR in customer display
  const updateCustomerDisplayWithQR = (qrString, amount) => {
    if (!customerDisplayWindow || customerDisplayWindow.closed) return;
    
    try {
      const qrContainer = customerDisplayWindow.document.getElementById('qrCodeContainer');
      if (qrContainer) {
        qrContainer.innerHTML = `
          <div style="text-align: center; padding: 30px; background: white; border-radius: 20px; box-shadow: 0 4px 20px rgba(0,0,0,0.3);">
            <h2 style="color: #167ac6; margin-bottom: 20px; font-size: 2em;">📱 Scan to Pay</h2>
            <div style="background: white; padding: 20px; border-radius: 15px; display: inline-block;">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrString)}" 
                   alt="QR Code" 
                   style="width: 300px; height: 300px; border: 3px solid #167ac6; border-radius: 10px;" />
            </div>
            <div style="margin-top: 20px; color: #333;">
              <p style="font-size: 1.5em; font-weight: bold; color: #167ac6; margin: 10px 0;">Amount: ฿${parseFloat(amount).toFixed(2)}</p>
              <p style="font-size: 1.2em; color: #666; margin: 10px 0;">Please scan QR code with your banking app</p>
            </div>
          </div>
        `;
        qrContainer.style.display = 'flex';
      }
    } catch (error) {
      console.error('Error updating customer display with QR:', error);
    }
  };

  // Helper function to clear QR from customer display
  const clearCustomerDisplayQR = () => {
    if (!customerDisplayWindow || customerDisplayWindow.closed) return;
    
    try {
      const qrContainer = customerDisplayWindow.document.getElementById('qrCodeContainer');
      if (qrContainer) {
        qrContainer.style.display = 'none';
        qrContainer.innerHTML = '';
      }
    } catch (error) {
      console.error('Error clearing customer display QR:', error);
    }
  };

  const showSCBQRModal = async (qrDataParam, billData, orderDetails) => {
    try {
      let generatedQrData = qrDataParam;
      
      // If no QR data provided, generate it using SCB API
      if (!qrDataParam || !(qrDataParam.qrRawData || qrDataParam.qrString || qrDataParam.deeplinkUrl)) {
        console.log('Generating new SCB QR code...');
        toast.info('Generating SCB QR Code...');
        
        const ref1 = orderDetails.orderId || `ORDER${Date.now()}`;
        const apiResponse = await generateSCBQRCode(getTotalAmount(), ref1, "POS1", "CHEFMATE");
        
        // Handle different response formats from different endpoints
        if (apiResponse.qrRawData || apiResponse.qrString) {
          // New API format
          generatedQrData = {
            qrRawData: apiResponse.qrRawData || apiResponse.qrString,
            qrString: apiResponse.qrString || apiResponse.qrRawData,
            ref1: ref1,
            amount: getTotalAmount(),
            transactionId: apiResponse.transactionId,
            isLegacy: false
          };
        } else if (apiResponse.deeplinkUrl) {
          // Legacy API format
          generatedQrData = {
            deeplinkUrl: apiResponse.deeplinkUrl,
            qrString: apiResponse.qrString || apiResponse.deeplinkUrl,
            qrRawData: apiResponse.qrString || apiResponse.deeplinkUrl,
            ref1: ref1,
            amount: getTotalAmount(),
            transactionId: apiResponse.transactionId,
            isLegacy: true
          };
        } else {
          throw new Error('Invalid QR response format');
        }
        
        // Store ref1 for status checking
        generatedQrData.ref1 = ref1;
      }
      
      // Set the data for the modal
      setQrData(generatedQrData);
      setQrBillData(billData);
      setQrOrderDetails(orderDetails);
      setShowQRModal(true);
      
      // Show QR in customer display
      const qrString = generatedQrData.qrRawData || generatedQrData.qrString || generatedQrData.deeplinkUrl;
      if (qrString) {
        updateCustomerDisplayWithQR(qrString, getTotalAmount());
      }
      
      toast.success('QR Code generated successfully!');
    } catch (error) {
      console.error('QR code generation failed:', error);
      toast.error('Failed to generate QR code. Please try again.');
      
      // Fallback to Thai QR code
      try {
        console.log('Falling back to Thai QR code...');
        const fallbackQrData = generateThaiQRCode(getTotalAmount(), orderDetails.orderId || `TEMP-${Date.now()}`);
        
        setQrData({
          qrRawData: fallbackQrData.qrString,
          ref1: orderDetails.orderId,
          amount: getTotalAmount(),
          isLegacy: true
        });
        setQrBillData(billData);
        setQrOrderDetails(orderDetails);
        setShowQRModal(true);
        
        toast.warn('Using fallback QR code');
      } catch (fallbackError) {
        console.error('Fallback QR generation failed:', fallbackError);
        toast.error('QR code generation failed completely. Please use cash or card payment.');
      }
    }
  };

  // Handle QR modal close
  const closeQRModal = () => {
    setShowQRModal(false);
    setQrData(null);
    setQrBillData(null);
    setQrOrderDetails(null);
    
    // Clear QR from customer display
    clearCustomerDisplayQR();
  };

  // Check payment status from QR modal
  const handleCheckPaymentStatus = async () => {
    if (!qrData || !qrOrderDetails) {
      toast.error('No payment data available');
      return;
    }

    try {
      toast.info('Checking payment status...');
      
      const ref1 = qrData.ref1 || qrOrderDetails.orderId;
      const statusResult = await checkSCBPaymentStatus(ref1);
      
      if (statusResult && statusResult.status === 'SUCCESS') {
        // Payment successful
        closeQRModal();
        await processSCBPaymentSuccess(qrBillData, qrOrderDetails, qrData);
        toast.success('Payment completed successfully!');
      } else {
        toast.warn('Payment not completed yet. Please complete the payment and try again.');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      toast.error('Failed to check payment status. Please try again.');
    }
  };

  // Handle payment simulation (for testing)
  const handleSimulatePayment = async () => {
    if (!qrData || !qrOrderDetails) {
      toast.error('No payment data available');
      return;
    }

    try {
      toast.info('Simulating payment...');
      
      const ref1 = qrData.ref1 || qrOrderDetails.orderId;
      await simulateSCBPayment(getTotalAmount(), ref1, "POS1", "CHEFMATE");
      
      toast.success('Payment simulated successfully!');
      
      // Check status after simulation
      setTimeout(() => {
        handleCheckPaymentStatus();
      }, 1000);
    } catch (error) {
      console.error('Payment simulation error:', error);
      toast.error('Failed to simulate payment.');
    }
  };

  // Bypass payment verification and continue to ticket generation
  const handleBypassVerification = async () => {
    if (!qrData || !qrOrderDetails) {
      toast.error('No payment data available');
      return;
    }

    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        'Are you sure you want to continue without verifying payment status?\n\n' +
        'This will generate a ticket assuming payment was successful.\n' +
        'Use this only for testing or when payment verification is not working.'
      );

      if (!confirmed) {
        return;
      }

      console.log('🚀 Bypassing payment verification and proceeding to ticket generation...');
      toast.info('Proceeding without payment verification...');

      // Close QR modal
      closeQRModal();

      // Create mock payment verification data
      const mockPaymentData = {
        ...qrData,
        transactionId: qrData.transactionId || qrOrderDetails.orderId,
        paidDateTime: new Date().toISOString(),
        status: 'BYPASSED', // Mark as bypassed for record keeping
        verificationMethod: 'MANUAL_BYPASS'
      };

      console.log('Using mock payment data:', mockPaymentData);

      // Process as successful payment (bypass verification)
      await processSCBPaymentSuccess(qrBillData, qrOrderDetails, mockPaymentData);
      
      toast.success('Payment bypassed and ticket generated successfully!');
      
    } catch (error) {
      console.error('Payment bypass error:', error);
      toast.error(`Failed to process bypassed payment: ${error.message || 'Unknown error'}`);
    }
  };

  // Load saved tickets from database
  const loadSavedTickets = async () => {
    try {
      setLoading(true);
      
      // Fetch bills from today and yesterday for recent tickets
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get('/billing/bills', {
        params: {
          start_date: yesterday,
          end_date: today,
          limit: 50,
          page: 1
        },
        ...getHeaders()
      });

      if (response.data.success && response.data.data) {
        const bills = response.data.data.bills || [];
        
        // Convert bills to ticket format for compatibility with existing functions
        const tickets = bills.map(bill => ({
          ticketId: `BILL-${bill.bill_number}`,
          billId: bill.bill_number,
          originalBillNumber: bill.bill_number,
          originalBillId: bill.id,
          timestamp: bill.created_at,
          billingDate: bill.bill_date,
          items: [], // Will be populated when needed
          total: parseFloat(bill.total_amount) || 0,
          category: 'Database Ticket',
          categories: [],
          paymentMethod: bill.payment_method,
          cashier: bill.cashier_name || 'Unknown',
          cashierId: bill.cashier_id || 'Unknown',
          customerName: bill.customer_name || 'Walk-in Customer',
          customerPhone: bill.customer_phone || null,
          isIndividual: false,
          isDatabaseTicket: true // Mark as database ticket
        }));

        // Sort by timestamp (newest first)
        const sortedTickets = tickets.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setSavedTickets(sortedTickets);
        
        toast.success(`Loaded ${sortedTickets.length} recent tickets from database`);
        return sortedTickets;
      } else {
        console.warn('No bills found in response:', response.data);
        setSavedTickets([]);
        return [];
      }
    } catch (error) {
      console.error('Error loading saved tickets from database:', error);
      toast.error('Failed to load tickets from database. Falling back to localStorage.');
      
      // Fallback to localStorage
      try {
        const localTickets = JSON.parse(localStorage.getItem('amusementParkTickets') || '[]');
        const sortedLocalTickets = localTickets.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setSavedTickets(sortedLocalTickets);
        return sortedLocalTickets;
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
        setSavedTickets([]);
        return [];
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle reprint last ticket
  const handleReprintLastTicket = async () => {
    try {
      const tickets = await loadSavedTickets();
      if (tickets.length === 0) {
        toast.warn('No tickets available to reprint');
        return;
      }

      const lastTicket = tickets[0]; // First item is the newest
      console.log('Reprinting last ticket:', lastTicket.ticketId);
      
      // Handle database tickets differently
      if (lastTicket.isDatabaseTicket) {
        await handleReprintSelectedTicket(lastTicket);
        return;
      }
      
      if (lastTicket.isIndividual && tickets.filter(t => t.originalBillNumber === lastTicket.originalBillNumber).length > 1) {
        // If it's an individual ticket and there are multiple tickets from the same bill, ask user
        const confirmed = window.confirm(
          `This was an individual ticket from Bill ${lastTicket.originalBillNumber}.\n\n` +
          `Would you like to reprint:\n` +
          `- OK: Just this individual ticket\n` +
          `- Cancel: All tickets from this bill`
        );
        
        if (confirmed) {
          // Reprint just this individual ticket
          printTicket(lastTicket);
          toast.success(`Individual ticket ${lastTicket.ticketId} reprinted successfully!`);
        } else {
          // Reprint all tickets from the same bill
          const billTickets = tickets.filter(t => t.originalBillNumber === lastTicket.originalBillNumber);
          printMultipleTickets(billTickets);
          toast.success(`All ${billTickets.length} tickets from Bill ${lastTicket.originalBillNumber} reprinted successfully!`);
        }
      } else {
        // Single ticket or combined ticket
        printTicket(lastTicket);
        toast.success(`Ticket ${lastTicket.ticketId} reprinted successfully!`);
      }
    } catch (error) {
      console.error('Error reprinting last ticket:', error);
      toast.error('Failed to reprint last ticket');
    }
  };

  // Handle show reprint modal
  const handleShowReprintModal = async () => {
    const tickets = await loadSavedTickets();
    if (tickets.length === 0) {
      toast.warn('No tickets available to reprint');
      return;
    }
    setShowReprintModal(true);
  };

  // Handle close reprint modal
  const handleCloseReprintModal = () => {
    setShowReprintModal(false);
  };

  // Load bill details with items from database
  const loadBillDetails = async (billId) => {
    try {
      const response = await axios.get(`/billing/bill/${billId}`, getHeaders());
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        console.warn('No bill details found:', response.data);
        return null;
      }
    } catch (error) {
      console.error('Error loading bill details:', error);
      return null;
    }
  };

  // Handle reprint selected ticket
  const handleReprintSelectedTicket = async (ticket) => {
    try {
      console.log('Reprinting selected ticket:', ticket.ticketId);
      
      // If it's a database ticket, load the full bill details first
      if (ticket.isDatabaseTicket) {
        setLoading(true);
        toast.info('Loading ticket details...');
        
        const billDetails = await loadBillDetails(ticket.originalBillId);
        
        if (!billDetails) {
          toast.error('Failed to load ticket details from database');
          setShowReprintModal(false);
          return;
        }

        // Convert bill items to ticket format
        const ticketData = {
          ...ticket,
          items: billDetails.items.map(item => ({
            id: item.ticket_id,
            item_name: item.item_name,
            category_id: item.category_id,
            category_name: item.category_name,
            price: parseFloat(item.unit_price) || 0,
            quantity: parseInt(item.quantity) || 1,
            total: parseFloat(item.total_price) || 0
          })),
          categories: [...new Set(billDetails.items.map(item => item.category_name).filter(Boolean))],
          category: billDetails.items.length === 1 
            ? billDetails.items[0].category_name 
            : billDetails.items.length > 1 
            ? 'Mixed Categories' 
            : 'General Admission'
        };

        // Ask user for printing preference if multiple items
        if (billDetails.items.length > 1) {
          const printSeparately = window.confirm(
            `This bill contains ${billDetails.items.length} items.\n\n` +
            `Would you like to:\n` +
            `- OK: Print separate tickets for each item\n` +
            `- Cancel: Print one combined ticket`
          );

          if (printSeparately) {
            // Create separate tickets for each item
            const separateTickets = billDetails.items.map((item, index) => ({
              ...ticketData,
              ticketId: `${ticket.ticketId}-${index + 1}`,
              items: [ticketData.items[index]],
              total: parseFloat(item.total_price) || 0,
              category: item.category_name || 'General Admission',
              categories: [item.category_name].filter(Boolean),
              isIndividual: true
            }));

            printMultipleTickets(separateTickets);
            toast.success(`${separateTickets.length} separate tickets reprinted successfully!`);
          } else {
            // Print combined ticket
            printTicket(ticketData);
            toast.success(`Combined ticket ${ticket.ticketId} reprinted successfully!`);
          }
        } else {
          // Single item ticket
          printTicket(ticketData);
          toast.success(`Ticket ${ticket.ticketId} reprinted successfully!`);
        }
      } else {
        // Local storage ticket - use existing logic
        if (ticket.isIndividual) {
          const tickets = await loadSavedTickets();
          const billTickets = tickets.filter(t => t.originalBillNumber === ticket.originalBillNumber);
          
          if (billTickets.length > 1) {
            // Ask user if they want to reprint just this ticket or all from the bill
            const confirmed = window.confirm(
              `This is an individual ticket from Bill ${ticket.originalBillNumber} with ${billTickets.length} total tickets.\n\n` +
              `Would you like to reprint:\n` +
              `- OK: Just this individual ticket\n` +
              `- Cancel: All ${billTickets.length} tickets from this bill`
            );
            
            if (confirmed) {
              printTicket(ticket);
              toast.success(`Individual ticket ${ticket.ticketId} reprinted successfully!`);
            } else {
              printMultipleTickets(billTickets);
              toast.success(`All ${billTickets.length} tickets from Bill ${ticket.originalBillNumber} reprinted successfully!`);
            }
          } else {
            printTicket(ticket);
            toast.success(`Ticket ${ticket.ticketId} reprinted successfully!`);
          }
        } else {
          printTicket(ticket);
          toast.success(`Ticket ${ticket.ticketId} reprinted successfully!`);
        }
      }
      
      setShowReprintModal(false);
    } catch (error) {
      console.error('Error reprinting selected ticket:', error);
      toast.error('Failed to reprint selected ticket');
    } finally {
      setLoading(false);
    }
  };

  // Process successful SCB payment
  const processSCBPaymentSuccess = async (billData, orderDetails, qrData) => {
    try {
      // Prepare payment data but DON'T save yet
      // Show modal first, then save only if user confirms
      const paymentData = {
        customer_name: billData.customer_name,
        customer_phone: billData.customer_phone,
        total_amount: getTotalAmount(),
        payment_method: 'qr',
        notes: `SCB QR Payment - Transaction ID: ${qrData.transactionId || orderDetails.orderId} - Ref: ${qrData.ref1 || 'N/A'} - Paid: ${qrData.paidDateTime || new Date().toISOString()}${qrData.verificationMethod ? ` - ${qrData.verificationMethod}` : ''}`,
        qr_reference: qrData.transactionId || orderDetails.orderId
      };

      toast.success('SCB QR payment completed successfully!');
      
      // Show ticket printing options modal (payment will be saved only if user selects a printing option)
      showTicketPrintingOptions(paymentData);
    } catch (error) {
      console.error('SCB payment processing error:', error);
      toast.error('Payment completed but failed to process. Please try again.');
    }
  };

  // Handle successful payment and generate ticket
  const handlePaymentSuccess = (billDetails) => {
    try {
      // Show ticket printing options
      showTicketPrintingOptions(billDetails);
    } catch (error) {
      console.error('Error handling payment success:', error);
      toast.error('Payment was successful but ticket generation failed. Please contact support.', {
        position: "top-right",
        autoClose: 8000,
      });
    }
  };

  // Save bill to database (called only after user selects printing option)
  const saveBillToDatabase = async (paymentData, isCombinedTicket = true) => {
    // Prevent duplicate saves - check if already saving (using ref for immediate check)
    if (isSavingBillRef.current) {
      console.warn('⚠️ Bill save already in progress, preventing duplicate');
      toast.warning('Please wait, processing previous request...');
      throw new Error('SAVE_IN_PROGRESS');
    }

    isSavingBillRef.current = true;
    console.log('🔒 isSavingBillRef set to TRUE');
    
    // Generate unique transaction ID for this save attempt
    const transactionId = `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🔵 Starting saveBillToDatabase - Transaction ID: ${transactionId}`, {
      isCombinedTicket,
      cartLength: cart.length,
      totalAmount: paymentData.total_amount
    });
    
    try {
      const cashierId = localStorage.getItem('uname') || sessionStorage.getItem('uname') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id');
      
      // For combined tickets: Keep items with their quantities (e.g., Adult x2)
      // For separate tickets: Expand each item to individual rows with quantity=1
      let itemsToSave;
      
      if (isCombinedTicket) {
        // Combined: Save as consolidated rows with actual quantities
        itemsToSave = cart.map(item => ({
          item_name: item.item_name,
          category_id: item.category_id || null,
          category_name: item.category_name || null,
          unit_price: parseFloat(item.price) || 0,
          quantity: parseInt(item.quantity) || 1,
          total_price: parseFloat(item.total) || 0,
          discount_per_item: 0,
          tax_per_item: 0,
          setup_date: billingDate || new Date().toISOString().slice(0, 10)
        }));
      } else {
        // Separate: Expand each item into individual rows with quantity=1
        itemsToSave = [];
        cart.forEach(item => {
          const itemPrice = parseFloat(item.price) || 0;
          const itemQuantity = parseInt(item.quantity) || 1;
          
          // Create separate row for each quantity
          for (let i = 0; i < itemQuantity; i++) {
            itemsToSave.push({
              item_name: item.item_name,
              category_id: item.category_id || null,
              category_name: item.category_name || null,
              unit_price: itemPrice,
              quantity: 1, // Each row has quantity 1
              total_price: itemPrice,
              discount_per_item: 0,
              tax_per_item: 0,
              setup_date: billingDate || new Date().toISOString().slice(0, 10)
            });
          }
        });
      }
      
      const billWithItemsData = {
        customer_name: paymentData.customer_name,
        customer_phone: paymentData.customer_phone,
        cashier_id: parseInt(cashierId) || null,
        cashier_name: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier',
        subtotal: parseFloat(paymentData.total_amount),
        tax_amount: 0,
        discount_amount: 0,
        total_amount: parseFloat(paymentData.total_amount),
        paid_amount: parseFloat(paymentData.total_amount),
        payment_method: paymentData.payment_method,
        payment_status: 'paid',
        bill_date: billingDate || new Date().toISOString().slice(0, 10),
        bill_time: new Date().toTimeString().slice(0, 8),
        status: 'active',
        notes: paymentData.notes || null,
        setup_date: billingDate || new Date().toISOString().slice(0, 10),
        ticket_print_mode: isCombinedTicket ? 'combined' : 'separate',
        items: itemsToSave,
        frontend_transaction_id: transactionId // Add frontend transaction ID for backend tracking
      };

      console.log(`📤 Sending request to backend - Transaction ID: ${transactionId}`, {
        items_count: itemsToSave.length,
        total_amount: parseFloat(paymentData.total_amount)
      });

      const billResponse = await axios.post(
        '/save-bill-with-items',
        billWithItemsData,
        getHeaders()
      );
      
      console.log(`📥 Received response from backend - Transaction ID: ${transactionId}`, {
        success: billResponse.data.success,
        bill_number: billResponse.data.bill_number,
        bill_id: billResponse.data.bill_id
      });
      
      if (billResponse.data.success) {
        const trackingRows = billResponse.data.barcode_tracking?.rows || [];
        const expectedTicketCount = isCombinedTicket
          ? 1
          : itemsToSave.length;

        if (trackingRows.length !== expectedTicketCount) {
          throw new Error(
            `Barcode tracking mismatch: expected ${expectedTicketCount}, got ${trackingRows.length}`
          );
        }

        console.log(`✅ Bill saved successfully - Transaction ID: ${transactionId}`, {
          bill_number: billResponse.data.bill_number,
          bill_id: billResponse.data.bill_id,
          items_count: billResponse.data.items_count
        });
        return {
          success: true,
          bill_id: billResponse.data.bill_id,
          bill_number: billResponse.data.bill_number,
          total_amount: paymentData.total_amount,
          payment_method: paymentData.payment_method,
          customer_name: paymentData.customer_name,
          customer_phone: paymentData.customer_phone,
          qr_reference: paymentData.qr_reference,
          transaction_id: transactionId,
          items_with_ticket_ids: billResponse.data.items_with_ticket_ids || [],
          tracking_rows: trackingRows
        };
      } else {
        throw new Error(billResponse.data.message || 'Failed to save bill');
      }
    } catch (error) {
      console.error(`❌ Error saving bill - Transaction ID: ${transactionId}`, error);
      
      // Handle 401 Unauthorized error - Token expired
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please login again.');
        // Clear user data
        clearUserData();
        // Logout and redirect to login page
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 1500);
        throw new Error('SESSION_EXPIRED');
      }
      
      // Handle duplicate bill error specifically
      if (error.response?.data?.code === 'ER_DUP_ENTRY') {
        toast.error('This bill has already been saved. Please check your transactions.');
        throw new Error('DUPLICATE_BILL');
      }
      
      throw error;
    } finally {
      // Always reset the flag after attempt (success or failure)
      isSavingBillRef.current = false;
      console.log('🔓 isSavingBillRef set to FALSE');
    }
  };

  // Show ticket printing options modal (BEFORE saving to database)
  const showTicketPrintingOptions = (paymentData) => {
    // Remove any existing modals first to prevent duplicates
    const existingModals = document.querySelectorAll('[data-ticket-modal="true"]');
    existingModals.forEach(modal => modal.remove());
    
    // Generate unique IDs for this modal instance to prevent conflicts
    const modalId = `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const btnCombinedId = `printCombined_${modalId}`;
    const btnSeparateId = `printSeparate_${modalId}`;
    const btnCombinedHTMLId = `printCombinedHTML_${modalId}`;
    const btnSeparateHTMLId = `printSeparateHTML_${modalId}`;
    const btnCancelId = `cancelTransaction_${modalId}`;
    
    console.log(`🎫 Creating modal with unique IDs - Modal ID: ${modalId}`);
    
    const modalHtml = `
      <div data-ticket-modal="true" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: 'Roboto', Arial, sans-serif;
      ">
        <div style="
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          max-width: 500px;
          width: 90%;
          text-align: center;
        ">
          <h3 style="
            color: #d90429;
            margin-bottom: 20px;
            font-size: 1.5rem;
          ">🎫 Ticket Printing Options</h3>
          
          <p style="
            margin-bottom: 25px;
            color: #666;
            font-size: 1rem;
          ">Choose how you want to print the tickets:</p>
          
          <div style="margin-bottom: 15px; padding: 10px; background: #e8f4fd; border-left: 4px solid #007bff; border-radius: 5px;">
            <strong style="color: #0056b3;">ESC/POS Thermal Printer</strong>
            <p style="font-size: 0.85rem; color: #555; margin-top: 5px;">Direct printing via printer server</p>
          </div>
          
          <div style="display: flex; gap: 15px; justify-content: center; flex-wrap: wrap; margin-bottom: 20px;">
            <button id="${btnCombinedId}" style="
              background: linear-gradient(135deg, #d90429, #b8032a);
              color: white;
              border: none;
              padding: 15px 25px;
              border-radius: 10px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(217, 4, 41, 0.3);
              transition: transform 0.2s;
            " onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
              📋 Combined Ticket<br>
              <small style="font-size: 0.8rem; opacity: 0.9;">ESC/POS - All items in one</small>
            </button>
            
            <button id="${btnSeparateId}" style="
              background: linear-gradient(135deg, #28a745, #20914a);
              color: white;
              border: none;
              padding: 15px 25px;
              border-radius: 10px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
              transition: transform 0.2s;
            " onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
              🎟️ Separate Tickets<br>
              <small style="font-size: 0.8rem; opacity: 0.9;">ESC/POS - One per item</small>
            </button>
          </div>
          
          <div style="margin-bottom: 15px; margin-top: 25px; padding: 10px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 5px;">
            <strong style="color: #856404;">🔄 Backup: HTML Thermal Print</strong>
            <p style="font-size: 0.85rem; color: #555; margin-top: 5px;">Use browser print if ESC/POS fails (80mm thermal paper)</p>
          </div>
          
          <div style="display: flex; gap: 15px; justify-content: center; flex-wrap: wrap; margin-bottom: 20px;">
            <button id="${btnCombinedHTMLId}" style="
              background: linear-gradient(135deg, #fd7e14, #dc6502);
              color: white;
              border: none;
              padding: 15px 25px;
              border-radius: 10px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(253, 126, 20, 0.3);
              transition: transform 0.2s;
            " onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
              📋 Combined Thermal<br>
              <small style="font-size: 0.8rem; opacity: 0.9;">HTML - All items in one</small>
            </button>
            
            <button id="${btnSeparateHTMLId}" style="
              background: linear-gradient(135deg, #17a2b8, #138496);
              color: white;
              border: none;
              padding: 15px 25px;
              border-radius: 10px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(23, 162, 184, 0.3);
              transition: transform 0.2s;
            " onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
              🎟️ Separate Thermal<br>
              <small style="font-size: 0.8rem; opacity: 0.9;">HTML - One per item</small>
            </button>
          </div>
          
          <div style="border-top: 1px solid #e0e0e0; padding-top: 15px;">
            <button id="${btnCancelId}" style="
              background: linear-gradient(135deg, #6c757d, #545b62);
              color: white;
              border: none;
              padding: 12px 30px;
              border-radius: 10px;
              font-size: 0.95rem;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
              transition: transform 0.2s;
            " onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
              ❌ Cancel Transaction
            </button>
          </div>
        </div>
      </div>
    `;

    // Create and show modal
    const modalDiv = document.createElement('div');
    modalDiv.innerHTML = modalHtml;
    document.body.appendChild(modalDiv);

    // Handle combined ticket option
    document.getElementById(btnCombinedId).onclick = async (event) => {
      // Prevent double-click immediately
      const button = event.target.closest('button');
      if (button.disabled) {
        console.warn('🚫 printCombined: Button already disabled, ignoring click');
        return;
      }
      
      console.log('🔵 printCombined: Button clicked, disabling...');
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
      
      try {
        toast.info('Saving transaction...');
        // Save to database first (combined: items with quantities)
        const savedBill = await saveBillToDatabase(paymentData, true);
        
        // Remove modal after save completes
        document.body.removeChild(modalDiv);
        
        // Then generate combined ticket
        await generateCombinedTicket(savedBill);
        toast.success('Transaction saved successfully!');
      } catch (error) {
        console.error('Error saving transaction:', error);
        toast.error('Failed to save transaction. Please try again.');
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
    };

    // Handle separate tickets option
    document.getElementById(btnSeparateId).onclick = async (event) => {
      // Prevent double-click immediately
      const button = event.target.closest('button');
      if (button.disabled) {
        console.warn('🚫 printSeparate: Button already disabled, ignoring click');
        return;
      }
      
    //  console.log('🔵 printSeparate: Button clicked, disabling...');
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
      
      try {
        toast.info('Saving transaction...');
        // Save to database first (separate: each item as individual row)
        const savedBill = await saveBillToDatabase(paymentData, false);
        
        // Remove modal after save completes
        document.body.removeChild(modalDiv);
        
        // Then generate separate tickets
        await generateSeparateTickets(savedBill);
        toast.success('Transaction saved successfully!');
      } catch (error) {
        console.error('Error saving transaction:', error);
        toast.error('Failed to save transaction. Please try again.');
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
    };

    // Handle combined HTML thermal backup
    document.getElementById(btnCombinedHTMLId).onclick = async (event) => {
      // Prevent double-click immediately
      const button = event.target.closest('button');
      if (button.disabled) {
        console.warn('🚫 printCombinedHTML: Button already disabled, ignoring click');
        return;
      }
      
      console.log('🔵 printCombinedHTML: Button clicked, disabling...');
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
      
      try {
        toast.info('Saving transaction...');
        // Save to database first (combined: items with quantities)
        const savedBill = await saveBillToDatabase(paymentData, true);
        
        // Remove modal after save completes
        document.body.removeChild(modalDiv);
        
        // Then generate combined ticket with HTML thermal print
        await generateCombinedTicketHTML(savedBill);
        toast.success('Transaction saved successfully!');
      } catch (error) {
        console.error('Error saving transaction:', error);
        toast.error('Failed to save transaction. Please try again.');
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
    };

    // Handle separate HTML thermal backup
    document.getElementById(btnSeparateHTMLId).onclick = async (event) => {
      // Prevent double-click immediately
      const button = event.target.closest('button');
      if (button.disabled) {
        console.warn('🚫 printSeparateHTML: Button already disabled, ignoring click');
        return;
      }
      
      console.log('🔵 printSeparateHTML: Button clicked, disabling...');
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
      
      try {
        toast.info('Saving transaction...');
        // Save to database first (separate: each item as individual row)
        const savedBill = await saveBillToDatabase(paymentData, false);
        
        // Remove modal after save completes
        document.body.removeChild(modalDiv);
        
        // Then generate separate tickets with HTML thermal print
        await generateSeparateTicketsHTML(savedBill);
        toast.success('Transaction saved successfully!');
      } catch (error) {
        console.error('Error saving transaction:', error);
        toast.error('Failed to save transaction. Please try again.');
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
    };

    // Handle cancel transaction
    document.getElementById(btnCancelId).onclick = () => {
      const confirmCancel = window.confirm(
        '⚠️ Are you sure you want to cancel this transaction?\n\n' +
        `Amount: ฿${paymentData.total_amount}\n` +
        `Payment Method: ${paymentData.payment_method}\n\n` +
        'NO data will be saved to the database.\n' +
        'The transaction will be completely cancelled.'
      );

      if (confirmCancel) {
        document.body.removeChild(modalDiv);
        clearCart();
        toast.warning('Transaction cancelled. No data was saved.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    };
  };

  // Generate combined ticket (original functionality)
  const generateCombinedTicket = async (billDetails) => {
    try {
      // Get unique categories from cart items
      const uniqueCategories = [...new Set(cart.map(item => item.category_name).filter(Boolean))];
      
      // Based on the categories table: Thai, VIP, Non-Thai
      const categoryDisplay = uniqueCategories.length === 1 
        ? uniqueCategories[0] 
        : uniqueCategories.length > 1 
        ? 'Mixed Categories' 
        : 'General Admission';
      
      // Additional category validation against known categories
      const validCategories = ['Thai', 'VIP', 'Non-Thai'];
      const validatedCategories = uniqueCategories.filter(cat => validCategories.includes(cat));
      const finalCategoryDisplay = validatedCategories.length > 0 
        ? (validatedCategories.length === 1 ? validatedCategories[0] : 'Mixed Categories')
        : categoryDisplay;

      // Generate combined ticket data with bill information
      const shortTicketId = generateShortId();
      const shortBillId = generateShortBillId();
      const combinedTrackingRow = (billDetails.tracking_rows || [])[0] || null;
      const verifyBarcode = combinedTrackingRow?.barcode_value || '';

      if (!verifyBarcode) {
        throw new Error('Missing barcode tracking for combined ticket');
      }

      const qrCodeDataUrl = await generateQRCodeDataUrl(verifyBarcode);
      const resolvedTicketId = combinedTrackingRow?.ticket_id || billDetails.items_with_ticket_ids?.[0]?.ticket_id || shortTicketId;

      const ticketData = {
        ticketId: resolvedTicketId,
        backendTicketId: resolvedTicketId,
        billId: shortBillId,
        originalBillNumber: billDetails.bill_number, // Keep original for reference
        originalBillId: billDetails.bill_id, // Keep original for reference
        timestamp: new Date().toISOString(), // Keep current timestamp for ticket printing
        billingDate: billingDate, // Add billing date for reference
        items: cart,
        total: billDetails.total_amount,
        category: finalCategoryDisplay,
        categories: validatedCategories.length > 0 ? validatedCategories : uniqueCategories,
        paymentMethod: billDetails.payment_method,
        cashier: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier',
        cashierId: localStorage.getItem('uid') || sessionStorage.getItem('uid') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id') || 'Unknown',
        customerName: billDetails.customer_name,
        customerPhone: billDetails.customer_phone,
        isIndividual: false,
        barcodeValue: verifyBarcode,
        qr_code_data: qrCodeDataUrl
      };

      // Save ticket to localStorage
      const savedTickets = JSON.parse(localStorage.getItem('amusementParkTickets') || '[]');
      savedTickets.push(ticketData);
      localStorage.setItem('amusementParkTickets', JSON.stringify(savedTickets));

      // Print combined ticket
      await printTicket(ticketData);

      // Clear cart after successful ticket generation
      clearCart();
      
      // Show success message
      toast.success(`Combined ticket for Bill ${billDetails.bill_number} generated successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (error) {
      console.error('Error generating combined ticket:', error);
      toast.error('Failed to generate combined ticket. Please try again.', {
        position: "top-right",
        autoClose: 8000,
      });
    }
  };

  // Generate separate tickets for each item
  const generateSeparateTickets = async (billDetails) => {
    try {
      const savedTickets = JSON.parse(localStorage.getItem('amusementParkTickets') || '[]');
      const ticketsGenerated = [];
      const trackingRows = billDetails.tracking_rows || [];
      let trackingIndex = 0;
      const expectedTickets = cart.reduce((sum, cartItem) => sum + (parseInt(cartItem.quantity, 10) || 1), 0);

      if (trackingRows.length !== expectedTickets) {
        throw new Error(`Missing barcode tracking rows for separate tickets. expected=${expectedTickets}, got=${trackingRows.length}`);
      }

      // Generate individual tickets for each item (considering quantity)
      for (const [index, item] of cart.entries()) {
        for (let i = 0; i < item.quantity; i++) {
          // Generate unique short ticket ID for each individual ticket
          const individualTicketId = generateShortId();
          const shortBillId = generateShortBillId();
          
          // Get category for this specific item
          const itemCategory = item.category_name || 'General Admission';
          const validCategories = ['Thai', 'VIP', 'Non-Thai'];
          const finalItemCategory = validCategories.includes(itemCategory) ? itemCategory : 'General Admission';

          const barcodeValue = trackingRows[trackingIndex]?.barcode_value || '';
          if (!barcodeValue) {
            throw new Error(`Missing barcode for separate ticket at index ${trackingIndex}`);
          }
          const qrCodeDataUrl = await generateQRCodeDataUrl(barcodeValue);

          const individualTicketData = {
            ticketId: trackingRows[trackingIndex]?.ticket_id || individualTicketId,
            backendTicketId: trackingRows[trackingIndex]?.ticket_id || null,
            billId: shortBillId,
            originalBillNumber: billDetails.bill_number, // Keep original for reference
            originalBillId: billDetails.bill_id, // Keep original for reference
            timestamp: new Date().toISOString(), // Keep current timestamp for ticket printing
            billingDate: billingDate, // Add billing date for reference
            items: [{
              ...item,
              quantity: 1,
              total: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0
            }],
            total: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
            category: finalItemCategory,
            categories: [finalItemCategory],
            paymentMethod: billDetails.payment_method,
            cashier: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier',
            cashierId: localStorage.getItem('uid') || sessionStorage.getItem('uid') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id') || 'Unknown',
            customerName: billDetails.customer_name,
            customerPhone: billDetails.customer_phone,
            isIndividual: true,
            itemNumber: `${index + 1}-${i + 1}`,
            totalItems: cart.reduce((sum, cartItem) => sum + cartItem.quantity, 0),
            barcodeValue,
            qr_code_data: qrCodeDataUrl
          };

          trackingIndex += 1;

          savedTickets.push(individualTicketData);
          ticketsGenerated.push(individualTicketData);
        }
      }

      // Save all tickets to localStorage
      localStorage.setItem('amusementParkTickets', JSON.stringify(savedTickets));

      // Print all individual tickets in one window
      await printMultipleTickets(ticketsGenerated);

      // Clear cart after successful ticket generation
      clearCart();
      
      // Show success message
      toast.success(`${ticketsGenerated.length} separate tickets for Bill ${billDetails.bill_number} generated successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (error) {
      console.error('Error generating separate tickets:', error);
      toast.error('Failed to generate separate tickets. Please try again.', {
        position: "top-right",
        autoClose: 8000,
      });
    }
  };

  // Generate combined ticket with HTML thermal print (backup)
  const generateCombinedTicketHTML = async (billDetails) => {
    try {
      const uniqueCategories = [...new Set(cart.map(item => item.category_name).filter(Boolean))];
      const categoryDisplay = uniqueCategories.length === 1 
        ? uniqueCategories[0] 
        : uniqueCategories.length > 1 
        ? 'Mixed Categories' 
        : 'General Admission';
      
      const validCategories = ['Thai', 'VIP', 'Non-Thai'];
      const validatedCategories = uniqueCategories.filter(cat => validCategories.includes(cat));
      const finalCategoryDisplay = validatedCategories.length > 0 
        ? (validatedCategories.length === 1 ? validatedCategories[0] : 'Mixed Categories')
        : categoryDisplay;

      const shortTicketId = generateShortId();
      const shortBillId = generateShortBillId();
      const combinedTrackingRow = (billDetails.tracking_rows || [])[0] || null;
      const verifyBarcode = combinedTrackingRow?.barcode_value || '';

      if (!verifyBarcode) {
        throw new Error('Missing barcode tracking for combined ticket');
      }

      const qrCodeDataUrl = await generateQRCodeDataUrl(verifyBarcode);
      const resolvedTicketId = combinedTrackingRow?.ticket_id || billDetails.items_with_ticket_ids?.[0]?.ticket_id || shortTicketId;

      const ticketData = {
        ticketId: resolvedTicketId,
        backendTicketId: resolvedTicketId,
        billId: shortBillId,
        originalBillNumber: billDetails.bill_number,
        originalBillId: billDetails.bill_id,
        timestamp: new Date().toISOString(),
        billingDate: billingDate,
        items: cart,
        bill_number: billDetails.bill_number,
        item_name: `Combined Ticket - ${cart.length} items`,
        quantity: cart.reduce((sum, item) => sum + item.quantity, 0),
        total: cart.reduce((sum, item) => sum + (typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0), 0),
        category: finalCategoryDisplay,
        categories: uniqueCategories,
        paymentMethod: billDetails.payment_method,
        cashier: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier',
        cashierId: localStorage.getItem('uid') || sessionStorage.getItem('uid') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id') || 'Unknown',
        customerName: billDetails.customer_name,
        customerPhone: billDetails.customer_phone,
        barcodeValue: verifyBarcode,
        qr_code_data: qrCodeDataUrl
      };

      const tickets = JSON.parse(localStorage.getItem('amusementParkTickets') || '[]');
      tickets.push(ticketData);
      localStorage.setItem('amusementParkTickets', JSON.stringify(tickets));

      printCombinedTicketThermalHTML(ticketData);

      clearCart();
      
      toast.success(`Combined ticket for Bill ${billDetails.bill_number} generated successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });

    } catch (error) {
      console.error('Error generating combined ticket HTML:', error);
      toast.error('Failed to generate combined ticket. Please try again.', {
        position: "top-right",
        autoClose: 8000,
      });
    }
  };

  // Generate separate tickets with HTML thermal print (backup)
  const generateSeparateTicketsHTML = async (billDetails) => {
    try {
      const savedTickets = JSON.parse(localStorage.getItem('amusementParkTickets') || '[]');
      const ticketsGenerated = [];
      const trackingRows = billDetails.tracking_rows || [];
      let trackingIndex = 0;
      const expectedTickets = cart.reduce((sum, cartItem) => sum + (parseInt(cartItem.quantity, 10) || 1), 0);

      if (trackingRows.length !== expectedTickets) {
        throw new Error(`Missing barcode tracking rows for separate thermal tickets. expected=${expectedTickets}, got=${trackingRows.length}`);
      }

      for (const [index, item] of cart.entries()) {
        const itemCategories = item.category_name ? [item.category_name] : [];
        const validCategories = ['Thai', 'VIP', 'Non-Thai'];
        const validatedItemCategories = itemCategories.filter(cat => validCategories.includes(cat));
        const finalItemCategory = validatedItemCategories.length > 0 
          ? validatedItemCategories[0] 
          : (item.category_name || 'General Admission');

        for (let i = 0; i < item.quantity; i++) {
          const shortTicketId = generateShortId();
          const shortBillId = generateShortBillId();
          
          const barcodeValue = trackingRows[trackingIndex]?.barcode_value || '';
          if (!barcodeValue) {
            throw new Error(`Missing barcode for separate thermal ticket at index ${trackingIndex}`);
          }
          const qrCodeDataUrl = await generateQRCodeDataUrl(barcodeValue);

          const individualTicketData = {
            ticketId: trackingRows[trackingIndex]?.ticket_id || shortTicketId,
            backendTicketId: trackingRows[trackingIndex]?.ticket_id || null,
            billId: shortBillId,
            originalBillNumber: billDetails.bill_number,
            originalBillId: billDetails.bill_id,
            timestamp: new Date().toISOString(),
            billingDate: billingDate,
            bill_number: billDetails.bill_number,
            item_name: item.item_name,
            quantity: 1,
            total: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
            category: finalItemCategory,
            categories: [finalItemCategory],
            paymentMethod: billDetails.payment_method,
            cashier: localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier',
            cashierId: localStorage.getItem('uid') || sessionStorage.getItem('uid') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id') || 'Unknown',
            customerName: billDetails.customer_name,
            customerPhone: billDetails.customer_phone,
            isIndividual: true,
            itemNumber: `${index + 1}-${i + 1}`,
            totalItems: cart.reduce((sum, cartItem) => sum + cartItem.quantity, 0),
            barcodeValue,
            qr_code_data: qrCodeDataUrl
          };

          trackingIndex += 1;

          savedTickets.push(individualTicketData);
          ticketsGenerated.push(individualTicketData);
        }
      }

      localStorage.setItem('amusementParkTickets', JSON.stringify(savedTickets));

      printMultipleTicketsThermalHTML(ticketsGenerated);

      clearCart();
      
      toast.success(`${ticketsGenerated.length} separate thermal tickets for Bill ${billDetails.bill_number} generated successfully!`, {
        position: "top-right",
        autoClose: 5000,
      });

    } catch (error) {
      console.error('Error generating separate tickets HTML:', error);
      toast.error('Failed to generate separate tickets. Please try again.', {
        position: "top-right",
        autoClose: 8000,
      });
    }
  };

  const loadImageFromDataUrl = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  // Generate canvas-based ESC/POS raster data for thermal printer
  const generateTicketCanvas = async (ticketData) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const dotsPerMM = 8;
    const qrLeftOverflowAllowance = 15 * dotsPerMM; // Must be > |leftMarginQR| to prevent clipping
    const contentWidth = 550 ; // Increased to prevent text cutting
    const contentHeight = 80 * dotsPerMM;
    
    canvas.width = contentHeight; // 640 pixels (80mm)
    canvas.height = contentWidth; // 800 pixels
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
    
    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    const col1X      = 33 * dotsPerMM;  // left column (+30mm right margin)
    const col2X      = 55 * dotsPerMM;  // right column (+22mm from col1)
    let yPos         = 20 * dotsPerMM;
    const labelGap   = 24;
    const lineHeight = 40;

    const currentDate = new Date(ticketData.timestamp);
    const formattedDateTime = `${String(currentDate.getDate()).padStart(2,'0')}/${String(currentDate.getMonth()+1).padStart(2,'0')} ${currentDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`;

    // ROW 1: TICKET ID | DATE & TIME
    ctx.font = 'bold 20px Arial';
    ctx.fillText('TICKET ID',   col1X, yPos);
    ctx.fillText('DATE ', col2X, yPos);
    yPos += labelGap;
    ctx.font = '20px Arial';
    ctx.fillText(ticketData.ticketId || 'N/A', col1X, yPos);
    ctx.fillText(formattedDateTime,             col2X, yPos);
    yPos += lineHeight;

    // ROW 2+: ITEM × QTY | PRICE
    if (ticketData.items && ticketData.items.length > 0) {
      ticketData.items.forEach(item => {
        const itemTotal = typeof item.total === 'number' ? item.total : (parseFloat(item.price || 0) * item.quantity);
        ctx.font = 'bold 20px Arial';
        ctx.fillText(`${item.item_name || item.name} X ${item.quantity}`, col1X, yPos);
        ctx.fillText(`฿${itemTotal}`, col2X, yPos);
        yPos += lineHeight;
      });
    }

    // ROW LAST: TOTAL AMOUNT | VALUE
    ctx.font = 'bold 20px Arial';
    ctx.fillText('TOTAL AMOUNT',         col1X, yPos);
    ctx.fillText(`฿${ticketData.total}`, col2X, yPos);

    // QR CODE — drawn below TOTAL AMOUNT
    const verifyValue = getTicketVerificationValue(ticketData);
    if (verifyValue) {
      yPos += lineHeight;
      const qrSize = 170;
      try {
        const qrDataUrl = await generateQRCodeDataUrl(verifyValue);
        if (qrDataUrl) {
          const qrImage = await loadImageFromDataUrl(qrDataUrl);
          ctx.drawImage(qrImage, col1X, yPos, qrSize, qrSize);
        }
      } catch (qrError) {
        console.error('Failed to render QR in ESC ticket canvas:', qrError);
      }
    }
    
    ctx.restore();
    
    return canvas;
  };

  // Convert canvas to ESC/POS commands
  const canvasToESCPOS = (canvas) => {
    const dotsPerMM = 8;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    const monoPixels = [];
    for (let i = 0; i < pixels.length; i += 4) {
      const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
      monoPixels.push(gray > 128 ? 0 : 1);
    }
    
    const rasterData = [];
    for (let y = 0; y < canvas.height; y++) {
      const rowBytes = [];
      for (let x = 0; x < canvas.width; x += 8) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          if (x + bit < canvas.width) {
            const pixelIndex = y * canvas.width + (x + bit);
            if (monoPixels[pixelIndex]) {
              byte |= (0x80 >> bit);
            }
          }
        }
        rowBytes.push(byte);
      }
      rasterData.push(...rowBytes);
    }
    
    const commands = [];
    commands.push(0x1B, 0x40); // Initialize
    
    // Add 33mm blank space before printing
    const feedAmount = Math.floor(33 * dotsPerMM / 8);
    if (feedAmount > 255) {
      const fullFeeds = Math.floor(feedAmount / 255);
      const remainder = feedAmount % 255;
      for (let i = 0; i < fullFeeds; i++) {
        commands.push(0x1B, 0x64, 255);
      }
      if (remainder > 0) {
        commands.push(0x1B, 0x64, remainder);
      }
    } else {
      commands.push(0x1B, 0x64, feedAmount);
    }
    
    // Send raster image
    const widthBytes = Math.ceil(canvas.width / 8);
    const widthL = widthBytes & 0xFF;
    const widthH = (widthBytes >> 8) & 0xFF;
    const heightL = canvas.height & 0xFF;
    const heightH = (canvas.height >> 8) & 0xFF;
    
    commands.push(0x1D, 0x76, 0x30, 0x00);
    commands.push(widthL, widthH);
    commands.push(heightL, heightH);
    commands.push(...rasterData);
    
    // Feed and cut
    commands.push(0x1B, 0x64, 0x03);
    commands.push(0x1D, 0x56, 0x42, 0x00);
    
    return new Uint8Array(commands);
  };

  // Send ESC/POS data to thermal printer
  const sendToThermalPrinter = async (escposData) => {
    try {
      const base64 = btoa(String.fromCharCode.apply(null, escposData));
      const response = await fetch('http://localhost:6001/print', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: base64 }),
      });
      
      if (!response.ok) {
        throw new Error('Printer server error');
      }
      
      const result = await response.json();
      console.log('Print result:', result);
      return true;
    } catch (error) {
      console.error('Failed to send to thermal printer:', error);
      toast.error('Failed to send to thermal printer. Make sure printer server is running.', {
        position: "top-right",
        autoClose: 5000,
      });
      return false;
    }
  };

  const printMultipleTickets = async (ticketsArray) => {
    // Use thermal printer with ESC/POS
    try {
      for (const ticketData of ticketsArray) {
        const canvas = await generateTicketCanvas(ticketData);
        const escposData = canvasToESCPOS(canvas);
        await sendToThermalPrinter(escposData);
        // Small delay between tickets
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      toast.success(`Successfully sent ${ticketsArray.length} ticket(s) to printer`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error printing tickets:', error);
      toast.error('Failed to print tickets. Check printer connection.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Backup HTML-based thermal printing functions (80mm width)
  const printMultipleTicketsThermalHTML = (ticketsArray) => {
    const printWindow = window.open('', '_blank');
    
    let allTicketsHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Thermal Tickets - The View Pattaya</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            background: white;
            padding: 0;
            margin: 0;
            width: 80mm;
          }
          
          .thermal-ticket {
            width: 80mm;
            padding: 5mm;
            background: white;
            page-break-after: always;
            page-break-inside: avoid;
            border-bottom: 2px dashed #000;
          }
          
          .thermal-ticket:last-child {
            page-break-after: auto;
            border-bottom: none;
          }
          
          .ticket-header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 3mm;
            margin-bottom: 3mm;
          }
          
          .ticket-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2mm;
          }
          
          .ticket-subtitle {
            font-size: 12px;
            margin-bottom: 1mm;
          }
          
          .ticket-body {
            margin-bottom: 3mm;
          }
          
          .ticket-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 12px;
          }
          
          .ticket-row-label {
            font-weight: bold;
          }
          
          .items-section {
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 3mm 0;
            margin: 3mm 0;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 13px;
            font-weight: 600;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #000;
            font-size: 14px;
            font-weight: bold;
          }
          
          .ticket-qr {
            text-align: center;
            margin: 3mm 0;
            padding: 3mm 0;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
          }
          
          .qr-code {
            width: 40mm;
            height: 40mm;
            margin: 0 auto;
          }
          
          .ticket-footer {
            text-align: center;
            font-size: 10px;
            border-top: 2px solid #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          
          .validity-box {
            background: #f0f0f0;
            border: 1px solid #000;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
            font-size: 11px;
            font-weight: bold;
          }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            body {
              width: 80mm;
            }
            
            .thermal-ticket {
              page-break-after: always !important;
              page-break-inside: avoid !important;
            }
            
            .thermal-ticket:last-child {
              page-break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
    `;

    ticketsArray.forEach((ticketData) => {
      const currentDate = new Date(ticketData.timestamp);
      const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000));
      
      allTicketsHtml += `
        <div class="thermal-ticket">
          <div class="ticket-header">
            <div class="ticket-title">🎡 THE VIEW PATTAYA</div>
            <div class="ticket-subtitle">TAX INVOICE/RECEIPT</div>
          </div>
          
          <div class="ticket-body">
            <div class="ticket-row">
              <span class="ticket-row-label">Bill No:</span>
              <span>${ticketData.bill_number || 'N/A'}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Date:</span>
              <span>${currentDate.toLocaleDateString('en-GB')}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Time:</span>
              <span>${currentDate.toLocaleTimeString('en-GB')}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Payment:</span>
              <span>${ticketData.paymentMethod || ticketData.payment_method || 'Cash'}</span>
            </div>
          </div>
          
          <div class="items-section">
            <div style="text-align: center; font-weight: bold; margin-bottom: 2mm; font-size: 12px;">ITEM</div>
            <div class="item-row">
              <span>${ticketData.item_name} x${ticketData.quantity}</span>
              <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
            </div>
              <div class="total-row">
              <span>VAT(7%):</span>
              <span>Included</span>
            </div>
            <div class="total-row">
              <span>TOTAL:</span>
              <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="ticket-qr">
            <img class="qr-code" src="${ticketData.qr_code_data}" alt="QR Code">
          </div>
          
          <div class="validity-box">
            VALID UNTIL: ${validTill.toLocaleString('en-GB')}
          </div>
          
          <div class="ticket-footer">
            <div>Thank you for visiting!</div>
            <div style="margin-top: 2mm; font-size: 14px;">
              Software developed By Cloudnet Softwares<br>
              +66-948712350 | +66-952477020
            </div>
          </div>
        </div>
      `;
    });

    allTicketsHtml += `
        <div class="no-print" style="text-align: center; margin-top: 10mm; padding: 5mm;">
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #007bff, #0056b3);
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
          ">
            🖨️ Print Tickets
          </button>
          <p style="margin-top: 10px; color: #666; font-size: 12px;">
            Ready to print ${ticketsArray.length} ticket(s)
          </p>
        </div>
        <script>
          // Auto-close window after printing
          window.onafterprint = function() {
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(allTicketsHtml);
    printWindow.document.close();
    
    toast.success('Thermal print preview opened. Click print button.', {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const printCombinedTicketThermalHTML = (ticketData) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date(ticketData.timestamp);
    const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000));
    
    let ticketHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Combined Thermal Ticket - The View Pattaya</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            background: white;
            padding: 0;
            margin: 0;
            width: 80mm;
          }
          
          .thermal-ticket {
            width: 80mm;
            padding: 5mm;
            background: white;
          }
          
          .ticket-header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 3mm;
            margin-bottom: 3mm;
          }
          
          .ticket-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2mm;
          }
          
          .ticket-subtitle {
            font-size: 12px;
            margin-bottom: 1mm;
          }
          
          .ticket-body {
            margin-bottom: 3mm;
          }
          
          .ticket-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 12px;
          }
          
          .ticket-row-label {
            font-weight: bold;
          }
          
          .items-section {
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 3mm 0;
            margin: 3mm 0;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 11px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #000;
            font-size: 14px;
            font-weight: bold;
          }
          
          .ticket-qr {
            text-align: center;
            margin: 3mm 0;
            padding: 3mm 0;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
          }
          
          .qr-code {
            width: 40mm;
            height: 40mm;
            margin: 0 auto;
          }
          
          .ticket-footer {
            text-align: center;
            font-size: 10px;
            border-top: 2px solid #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          
          .validity-box {
            background: #f0f0f0;
            border: 1px solid #000;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
            font-size: 11px;
            font-weight: bold;
          }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            body {
              width: 80mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="thermal-ticket">
          <div class="ticket-header">
            <div class="ticket-title">🎡 THE VIEW PATTAYA</div>
            <div class="ticket-subtitle">Tax Invoice/Receipt</div>
          </div>
          
          <div class="ticket-body">
            <div class="ticket-row">
              <span class="ticket-row-label">Bill No:</span>
              <span>${ticketData.bill_number || 'N/A'}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Date:</span>
              <span>${currentDate.toLocaleDateString('en-GB')}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Time:</span>
              <span>${currentDate.toLocaleTimeString('en-GB')}</span>
            </div>
            <div class="ticket-row">
              <span class="ticket-row-label">Payment:</span>
              <span>${ticketData.paymentMethod || ticketData.payment_method || 'Cash'}</span>
            </div>
          </div>
          
          <div class="items-section">
            <div style="text-align: center; font-weight: bold; margin-bottom: 2mm; font-size: 12px;">ITEMS</div>
            ${ticketData.items && ticketData.items.length > 0 ? ticketData.items.map(item => `
              <div class="item-row">
                <span>${item.item_name} x${item.quantity}</span>
                <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
              </div>
            `).join('') : `
              <div class="item-row">
                <span>${ticketData.item_name || 'Ticket'} x${ticketData.quantity || 1}</span>
                <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
              </div>
            `}
            
           
             <div class="total-row">
              <span>VAT(7%):</span>
              <span>Included</span>
            </div>
            <div class="total-row">
              <span>TOTAL:</span>
              <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="ticket-qr">
            <img class="qr-code" src="${ticketData.qr_code_data}" alt="QR Code">
          </div>
          
          <div class="validity-box">
            VALID UNTIL: ${validTill.toLocaleString('en-GB')}
          </div>
          
          <div class="ticket-footer">
            <div style="font-size: 14px; font-weight: bold; margin-bottom: 2mm;">Thank you for visiting!</div>
            <div style="margin-top: 3mm; font-size: 13px; font-weight: 600; line-height: 1.5;">
              Software developed By Cloudnet Softwares<br>
              +66-948712350 | +66-952477020
            </div>
          </div>
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 10mm; padding: 5mm;">
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #007bff, #0056b3);
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
          ">
            🖨️ Print Ticket
          </button>
          <p style="margin-top: 10px; color: #666; font-size: 12px;">
            Ready to print combined ticket
          </p>
        </div>
        <script>
          // Auto-close window after printing
          window.onafterprint = function() {
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(ticketHtml);
    printWindow.document.close();
    
    toast.success('Thermal print preview opened. Click print button.', {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const printMultipleTickets_OLD = (ticketsArray) => {
    const printWindow = window.open('', '_blank');
    
    // Generate HTML for all tickets
    let allTicketsHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Amusement Park Boarding Passes</title>
        <meta charset="UTF-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Roboto', Arial, sans-serif; 
            background: white;
            padding: 0;
            margin: 0;
          }
          
          .tickets-container {
            gap: 0;
          }
          
          /* Fan-folded horizontal boarding pass - 215mm x 80mm */
          .boarding-pass {
            width: 215mm;
            height: 80mm;
            background: white;
            position: relative;
            overflow: hidden;
            font-size: 8px;
            page-break-after: always;
            page-break-inside: avoid;
            margin-bottom: 10mm;
            box-sizing: border-box;
          }
          
          .boarding-pass:last-child {
            page-break-after: auto;
            margin-bottom: 0;
          }
          
          /* Left section - Main ticket info */
          .main-section {
            width: 160mm;
            position: relative;
            float: left;
            height: 80mm;
            padding-left: 2mm;
          }
          
          
          /* Right section - Stub/Coupon */
          .stub-section {
            width: 55mm;
            position: relative;
            float: right;
            height: 80mm;
            overflow: hidden;
          }
          
          /* Fan-fold perforations */
          .stub-section::before {
            content: '';
            position: absolute;
            left: -1px;
            top: 0;
            bottom: 0;
            width: 2px;
          
          }
          
          .header-section {
            color: black;
            padding: 4mm 4mm;
            position: relative;
            height: 18mm;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .header-left {
            flex: 1;
          }
          
          .park-name {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 1mm;
            color: black;
          }
          
          .park-subtitle {
            font-size: 9px;
            opacity: 0.9;
            font-weight: 300;
          }
          
          .category-display {
            font-size: 14px;
            font-weight: 700;
            margin-top: 2mm;
            padding: 1mm 4mm;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: black;
          }
          
          .header-right {
            text-align: right;
            font-size: 8px;
          }
          
          .ticket-id-display {
            font-family: 'Courier New', monospace;
            font-weight: 700;
            font-size: 9px;
          }
          
          .content-section {
            padding: 2mm 4mm;
            flex: 1;
            display: flex;
            gap: 3mm;
          }
          
          .left-content {
            flex: 1;
          }
          
          .right-content {
            width: 35mm;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2mm 3mm;
            margin-bottom: 2mm;
          }
          
          .info-item {
            min-height: 6mm;
          }
          
          .info-label {
            font-size: 12px;
            font-weight: 700;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 0.5mm;
          }
          
          .info-value {
            font-size: 12px;
            font-weight: 600;
            color: #2c3e50;
            line-height: 1.1;
          }
          
          .items-compact {
            border-radius: 2mm;
            padding: 2mm;
            border-left: 1.5mm solid black;
            margin-bottom: 2mm;
          }
          
          .items-title {
            font-size: 9px;
            font-weight: 700;
            color: black;
            margin-bottom: 1mm;
            text-transform: uppercase;
          }
          
          .item-compact {
            display: flex;
            justify-content: space-between;
            font-size: 8px;
            margin-bottom: 0.5mm;
            padding: 0.3mm 0;
          }
          
          .item-compact:last-of-type {
            border-top: 1px solid black;
            padding-top: 1mm;
            margin-top: 1mm;
            font-weight: 700;
            color: black;
            font-size: 7px;
          }
          
          .qrcode-compact {
            text-align: center;
            background: white;
            
            padding: 1.5mm;
          }
          
          .qrcode-label {
            font-size: 10px;
            color: #666;
            margin-bottom: 0.5mm;
            text-transform: uppercase;
            font-weight: 600;
          }
          
          /* Ferris Wheel Section Styling */
          .ferris-wheel-section {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .ferris-wheel-container {
            text-align: center;
            padding: 3mm;
            background: linear-gradient(135deg, #ff6b6b, #4ecdc4);
            border-radius: 5mm;
            color: white;
            box-shadow: 0 3mm 6mm rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .ferris-wheel {
            font-size: 48px;
            animation: rotate 3s linear infinite;
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Stub section styling */
          .stub-header {
            color: black;
            padding: 4mm;
            text-align: center;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-bottom: 2mm;
          }
          
          .stub-title {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 0.5mm;
          }
          
          .stub-subtitle {
            font-size: 13px;
            opacity: 0.8;
          }
          
          .stub-content {
            padding: 3mm;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-top: 7mm;
          }
          
          .stub-info-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0mm;
            margin-bottom: 2mm;
          }
          
          .stub-info-item {
            min-height: 8mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 1mm;
            padding-bottom: 1mm;
            border-bottom: none;
          }
          
          .stub-info-item:last-child {
            border-bottom: none;
          }
          
          .stub-info {
            margin-bottom: 2mm;
          }
          
          .stub-label {
            font-size: 14px;
            font-weight: 700;
            color: #080808FF;
            text-transform: uppercase;
            margin-bottom: 0.5mm;
          }
          
          .stub-value {
            font-size: 14px;
            font-weight: 300;
            color: #080808FF;
          }
          
          .validity-compact {
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 2mm;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
          }
          
          .validity-text {
            color: #856404;
            font-weight: 600;
            font-size: 7px;
            line-height: 1.3;
          }
          
          .footer-compact {
            background: #FFFFFFFF;
            color: black;
            padding: 0mm;
            text-align: center;
            font-size: 11px;
            line-height: 1.3;
            opacity: 0.9;
          }
          

          
          /* Print page setup for multiple tickets */
          @page {
            size: 215mm 80mm;
            margin: 0;
          }
          
          /* Print styles for multiple tickets */
          @media print { 
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            html, body { 
              background: white; 
              padding: 0; 
              margin: 0;
              width: auto;
              height: auto;
              overflow: visible;
            }
            
            .tickets-container {
              width: auto;
              height: auto;
            }
            
            .boarding-pass {
              width: 215mm !important;
              height: 80mm !important;
              box-shadow: none;
              page-break-after: always !important;
              page-break-inside: avoid !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: scale(1) !important;
              display: block !important;
            }
            
            .boarding-pass:last-child {
              page-break-after: auto !important;
            }
            
            .main-section {
              width: 160mm !important;
              height: 80mm !important;
            }
            
            .stub-section {
              width: 55mm !important;
              height: 80mm !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="tickets-container">
    `;

    // Add each ticket to the HTML
    ticketsArray.forEach((ticketData) => {
      const currentDate = new Date(ticketData.timestamp);
      const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000)); // 1 hour validity
      
      allTicketsHtml += generateSingleTicketHTML(ticketData, currentDate, validTill);
    });

    // Close the container and add print button
    allTicketsHtml += `
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 30px; position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: white; padding: 20px; border-radius: 10px; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
          <p style="margin-bottom: 15px; color: #666; font-size: 1rem;">
            <strong>${ticketsArray.length} Individual Tickets Ready</strong><br>
            <small>Each ticket can be used separately for entry</small>
          </p>
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #d90429, #b8032a);
            color: white;
            border: none;
            padding: 15px 40px;
            border-radius: 25px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(217, 4, 41, 0.3);
          ">🖨️ Print All ${ticketsArray.length} Tickets</button>
          <button onclick="window.close()" style="
            background: linear-gradient(135deg, #6c757d, #495057);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          ">✕ Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(allTicketsHtml);
    printWindow.document.close();
  };

  // Helper function to generate HTML for a single ticket
  const generateSingleTicketHTML1 = (ticketData, currentDate, validTill) => {
    return `
      <div class="boarding-pass">
        <!-- Main Section (Left) - BLANK -->
         <div class="main-section">
          <div class="header-section">
            <div class="header-left">
              <div class="park-name">🎡 The View Pattaya</div>
              <div class="park-subtitle">Boarding Pass • Entry Ticket${ticketData.isIndividual ? ` • Individual Ticket ${ticketData.itemNumber}/${ticketData.totalItems}` : ''}</div>
            </div>

            <div class="header-right">
              <div class="ticket-id-display">${ticketData.ticketId}</div>
              ${ticketData.isIndividual ? `<div style="font-size: 8px; margin-top: 2mm; color: rgba(255,255,255,0.8);">Item: ${ticketData.items[0].item_name}</div>` : ''}
            </div>
          </div>
          
          <div class="content-section">
            <div class="left-content">
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Issue Date</div>
                  <div class="info-value">${currentDate.toLocaleDateString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Issue Time</div>
                  <div class="info-value">${currentDate.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Valid Until</div>
                  <div class="info-value">${validTill.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Payment Method</div>
                  <div class="info-value">${ticketData.paymentMethod}</div>
                </div>
              </div>
              
              <div class="items-compact">
                <div class="items-title">🎫 Attractions & Services</div>
                ${ticketData.items.map(item => `
                  <div class="item-compact">
                    <span>${item.item_name} × ${item.quantity}</span>
                    <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
                  </div>
                `).join('')}
                <div class="item-compact">
                  <span>TICKET AMOUNT</span>
                  <span>฿${ticketData.total}</span>
                </div>
              </div>
              
              <div class="validity-compact">
                <div class="validity-text">
                  ⏰ Valid for 1 HOUR • Present at each attraction
                </div>
              </div>
            </div>
            
            <div class="right-content">
              <div class="ferris-wheel-section">
                <div class="ferris-wheel-container">
                  <div class="ferris-wheel">
                    🎡
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="footer-compact">
            🎢 The View Pattaya • Keep with you at all times • Non-transferable • No refunds
          </div>
        </div>
        
      
        
        
           
        <!-- Stub Section (Right) -->
        <div class="stub-section">
          <div class="stub-content">
            <div class="stub-info-grid">
              <div class="stub-info-item">
                <div class="stub-label">TICKET ID</div>
                <div class="stub-value">${ticketData.ticketId}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">DATE & TIME</div>
                <div class="stub-value">${currentDate.toLocaleDateString()}<br>${currentDate.toLocaleTimeString()}</div>
              </div>
              
              ${ticketData.items && ticketData.items.length > 0 ? ticketData.items.map(item => `
              <div class="stub-info-item">
                <div class="stub-label">${item.item_name || item.name || ''} x ${item.quantity}</div>
                <div class="stub-value">฿${typeof item.total === 'number' ? item.total : (typeof item.price === 'number' ? item.price * item.quantity : parseFloat(item.price || 0) * item.quantity)}</div>
              </div>
              `).join('') : ''}
              
              <div class="stub-info-item">
                <div class="stub-label">TOTAL AMOUNT</div>
                <div class="stub-value">฿${ticketData.total}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  };
 const generateSingleTicketHTML = (ticketData, currentDate, validTill) => {
    return `
      <div class="boarding-pass">
        <!-- Main Section (Left) - BLANK -->
        <div class="main-section">
          <div class="header-section">
            <div class="header-left">
              <div class="park-name">🎡 The View Pattaya</div>
              <div class="park-subtitle">Boarding Pass • Entry Ticket${ticketData.isIndividual ? ` • Individual Ticket ${ticketData.itemNumber}/${ticketData.totalItems}` : ''}</div>
            </div>
            <div class="header-right">
              <div class="ticket-id-display">${ticketData.ticketId}</div>
              ${ticketData.isIndividual ? `<div style="font-size: 8px; margin-top: 2mm; color: rgba(255,255,255,0.8);">Item: ${ticketData.items[0].item_name}</div>` : ''}
            </div>
          </div>
          
          <div class="content-section">
            <div class="left-content">
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Issue Date</div>
                  <div class="info-value">${currentDate.toLocaleDateString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Issue Time</div>
                  <div class="info-value">${currentDate.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Valid Until</div>
                  <div class="info-value">${validTill.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Payment Method</div>
                  <div class="info-value">${ticketData.paymentMethod}</div>
                </div>
              </div>
              
              <div class="items-compact">
                <div class="items-title">🎫 Attractions & Services</div>
                ${ticketData.items.map(item => `
                  <div class="item-compact">
                    <span>${item.item_name} × ${item.quantity}</span>
                    <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
                  </div>
                `).join('')}
                <div class="item-compact">
                  <span>TICKET AMOUNT</span>
                  <span>฿${ticketData.total}</span>
                </div>
              </div>
              
              <div class="validity-compact">
                <div class="validity-text">
                  ⏰ Valid for 1 HOUR • Present at each attraction
                </div>
              </div>
            </div>
            
            <div class="right-content">
              <div class="ferris-wheel-section">
                <div class="ferris-wheel-container">
                  <div class="ferris-wheel">
                    🎡
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="footer-compact">
            🎢 The View Pattaya • Keep with you at all times • Non-transferable • No refunds
          </div>
        </div>
        
      
        
        
        <!-- Stub Section (Right) -->
        <div class="stub-section">
          <div class="stub-content">
            <div class="stub-info-grid">
              <div class="stub-info-item">
                <div class="stub-label">TICKET ID</div>
                <div class="stub-value">${ticketData.ticketId}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">DATE & TIME</div>
                <div class="stub-value">${currentDate.toLocaleDateString()}<br>${currentDate.toLocaleTimeString()}</div>
              </div>
              
              ${ticketData.items && ticketData.items.length > 0 ? ticketData.items.map(item => `
              <div class="stub-info-item">
                <div class="stub-label">${item.item_name || item.name || ''} x ${item.quantity}</div>
                <div class="stub-value">฿${typeof item.total === 'number' ? item.total : (typeof item.price === 'number' ? item.price * item.quantity : parseFloat(item.price || 0) * item.quantity)}</div>
              </div>
              `).join('') : ''}
              
              <div class="stub-info-item">
                <div class="stub-label">TOTAL AMOUNT</div>
                <div class="stub-value">฿${ticketData.total}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  };
  const printTicket = async (ticketData) => {
    // Use thermal printer with ESC/POS
    try {
      const canvas = await generateTicketCanvas(ticketData);
      const escposData = canvasToESCPOS(canvas);
      const success = await sendToThermalPrinter(escposData);
      
      if (success) {
        toast.success('Ticket sent to printer successfully', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error printing ticket:', error);
      toast.error('Failed to print ticket. Check printer connection.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const printTicket_OLD = (ticketData) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date(ticketData.timestamp);
    const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000)); // 1 hour validity

    const ticketHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Amusement Park Boarding Pass</title>
        <meta charset="UTF-8">
        <style>
          /* Same styles as in printMultipleTickets */
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Roboto', Arial, sans-serif; 
            background: white;
            padding: 0;
            margin: 0;
          }
          
          /* Fan-folded horizontal boarding pass - 215mm x 80mm */
          .boarding-pass {
            width: 215mm;
            height: 80mm;
            background: white;
            
            position: relative;
            overflow: hidden;
            font-size: 10px;
            page-break-inside: avoid;
            box-sizing: border-box;
          }
          
          /* Left section - Main ticket info */
          .main-section {
            width: 160mm;
            position: relative;
            float: left;
            height: 80mm;
          }
          
          /* Right section - Stub/Coupon */
          .stub-section {
            width: 55mm;
            position: relative;
            float: right;
            height: 80mm;
            overflow: hidden;
            
          }
          
          /* Fan-fold perforations */
          .stub-section::before {
            content: '';
            position: absolute;
            left: -1px;
            top: 0;
            bottom: 0;
            width: 2px;
            
          }
          
          .header-section {
           
            color: black;
            padding: 8mm 6mm;
            position: relative;
            height: 25mm;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .header-left {
            flex: 1;
          }
          
          .park-name {
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 2mm;
            color: black;
          }
          
          .park-subtitle {
            font-size: 11px;
            opacity: 0.9;
            font-weight: 300;
          }
          
          .category-display {
            font-size: 16px;
            font-weight: 700;
            margin-top: 3mm;
            padding: 2mm 6mm;
           
            border-radius: 10mm;
           
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: black;
          }
          
          .header-right {
            text-align: right;
            font-size: 8px;
          }
          
          .ticket-id-display {
            font-family: 'Courier New', monospace;
            font-weight: 700;
            font-size: 10px;
          }
          
          .content-section {
            padding: 4mm 6mm;
            flex: 1;
            display: flex;
            gap: 4mm;
          }
          
          .left-content {
            flex: 1;
          }
          
          .right-content {
            width: 35mm;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3mm 4mm;
            margin-bottom: 4mm;
          }
          
          .info-item {
            min-height: 8mm;
          }
          
          .info-label {
            font-size: 12px;
            font-weight: 700;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 1mm;
          }
          
          .info-value {
            font-size: 11px;
            font-weight: 600;
            color: #2c3e50;
            line-height: 1.2;
          }
          
          .items-compact {
            background: #f8f9fa;
            border-radius: 3mm;
            padding: 3mm;
            border-left: 2mm solid #d90429;
            margin-bottom: 3mm;
          }
          
          .items-title {
            font-size: 11px;
            font-weight: 700;
            color: #d90429;
            margin-bottom: 2mm;
            text-transform: uppercase;
          }
          
          .item-compact {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            margin-bottom: 1mm;
            padding: 0.5mm 0;
          }
          
          .item-compact:last-of-type {
            border-top: 1px solid #d90429;
            padding-top: 1.5mm;
            margin-top: 1.5mm;
            font-weight: 700;
            color: #d90429;
            font-size: 8px;
          }
          
          .qrcode-compact {
            text-align: center;
            background: white;
            
            border-radius: 2mm;
            padding: 2mm;
          }
          
          .qrcode-label {
            font-size: 6px;
            color: #666;
            margin-bottom: 1mm;
            text-transform: uppercase;
            font-weight: 600;
          }
          
          .qrcode-2d {
            font-family: 'Courier New', monospace;
            font-size: 7px;
            line-height: 0.4;
            margin: 1mm 0;
            color: #000;
            white-space: pre;
            display: inline-block;
          }
          
          .qrcode-id {
            font-size: 6px;
            color: #666;
            margin-top: 1mm;
            font-family: 'Courier New', monospace;
          }
          
          /* Blank section styling */
          .blank-section {
            width: 100mm;
            position: relative;
            float: left;
            height: 100%;
            background: white;
            border-left: 1px dashed #ccc;
            padding: 5mm;
          }
          
          .blank-section-label {
            font-size: 12px;
            color: #999;
            text-align: center;
            margin-top: 5mm;
            opacity: 0.5;
          }
          
          /* Stub section styling */
          .stub-header {
            
            color: black;
            padding: 4mm;
            text-align: center;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .stub-title {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 1mm;
          }
          
          .stub-subtitle {
            font-size: 13px;
            opacity: 0.8;
          }
          
          .stub-content {
            padding: 1mm;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-top: 7mm;
          }
          .stub-info-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0mm;
            margin-bottom: 2mm;
          }
            
          .stub-info {
            margin-bottom: 2mm;
          }
          
          .stub-label {
            font-size: 14px;
            font-weight: 700;
            color: #080808FF;
            text-transform: uppercase;
            margin-bottom: 0.5mm;
          }
          
          .stub-value {
            font-size: 14px;
            font-weight: 300;
            color: #080808FF;
          }
          
          .validity-compact {
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 2mm;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
          }
          
          .validity-text {
            color: #856404;
            font-weight: 600;
            font-size: 7px;
            line-height: 1.3;
          }
          
          .footer-compact {
            background: #FAFCFEFF;
            color: black;
            padding: 0mm;
            text-align: center;
            font-size: 11px;
            line-height: 1.3;
            opacity: 0.9;
          }
          
          /* Print page setup for exact dimensions */
          @page {
            size: 215mm 80mm;
            margin: 0;
          }
          
          /* Print styles for single ticket */
   @media print {
  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .no-print {
    display: none !important;
  }

  @page {
    /* Landscape ticket: 202mm wide x 80mm high */
    size: 202mm 80mm;
    margin: 0; /* no default page margin */
  }

  html, body {
    background: white;
    margin: 0;
    padding: 0;
    width: 202mm;
    height: 80mm;
    overflow: hidden;
  }

  .boarding-pass {
    width: 202mm !important;
    height: 80mm !important;
    box-shadow: none !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: none !important;
    page-break-inside: avoid;
  }
}


            
            .main-section {
              width: 160mm !important;
              height: 80mm !important;
            }
            
            .stub-section {
              width: 55mm !important;
              height: 80mm !important;
            }
          }
        </style>
      </head>
      <body>
        ${generateSingleTicketHTML(ticketData, currentDate, validTill)}
        
        <div class="no-print" style="text-align: center; margin-top: 30px;">
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #d90429, #b8032a);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(217, 4, 41, 0.3);
          ">🖨️ Print Ticket</button>
          <button onclick="window.close()" style="
            background: linear-gradient(135deg, #6c757d, #495057);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          ">✕ Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(ticketHTML);
    printWindow.document.close();

  };

  return (
    <>
      {isCashier ? 
      (
        // Cashier Layout - No sidebar/topbar
        <div className="cashier-full-layout">
          <ToastContainer />
          
        
          
          {/* Counter Closed Modal */}
          {showCounterClosedModal && (
            <div className="modal-overlay">
              <div className="counter-closed-modal">
                <div className="modal-header">
                  <h3>🚫 Counter Already Closed</h3>
                </div>
                <div className="modal-body">
                  <div className="counter-closed-icon-large">🔒</div>
                  <p className="counter-closed-message-text">
                    Your cashier counter has already been closed for today.
                  </p>
                  <p className="counter-status-info">
                    {counterStatusMessage.replace('Redirecting to summary page...', '')}
                  </p>
                  <div className="redirect-info">
                    <p>Redirecting to Cashier Summary in:</p>
                    <div className="countdown-display">
                      <span className="countdown-number">{redirectCountdown}</span>
                      <span className="countdown-text">seconds</span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    onClick={() => navigate('/pos/cashier-summary')}
                    className="modal-btn primary-btn"
                  >
                    Go to Summary Now
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Cashier Header */}
          <div className="cashier-header">
            <h2 className="cashier-title">🎡 The View Ticket Billing</h2>
            <div className="cashier-user-info">
             
           
              <span className="billing-date-label">📅  Date:</span>
              <span className="billing-date-value">
                {getNextDayDate()}
              </span>
              <span className="cashier-id-label">👤 Cashier ID:</span>
              <span className="cashier-id-value">{cashierId || 'Not Available'}</span>

            
            </div>
          </div>
          
          <div className="container-fluid">
            <div className="row">
              {/* Main Content - Categories and Tickets */}
              <div className="col-lg-8 col-md-8 col-sm-12">
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <h5>Loading tickets...</h5>
                  </div>
                ) : (
                  <>
                    {/* Category Selection Cards */}
                    <div className="panel panel-default card-view">
                      
                      <div className="panel-body" style={{ padding: '20px' }}>
                        {loading ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>Loading categories...</p>
                          </div>
                        ) : categories.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>No categories found. Please check your database connection.</p>
                          </div>
                        ) : (
                          <div className="categories-grid-container">
                            {categories.map((category) => (
                              <div 
                                key={category.id}
                                className={`category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                                onClick={() => handleCategorySelect(category.id)}
                              >
                                <div className="category-emoji">🎠</div>
                                <div className="category-title">{category.name}</div>
                                <div className="category-description">
                                  {ticketPricing[category.id]?.length || 0} tickets
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tickets Section */}
                    {selectedCategory && (
                      <div className="panel panel-default card-view category-section">
                        
                        <div className="panel-body" style={{ padding: '20px' }}>
                          {ticketPricing[selectedCategory] && ticketPricing[selectedCategory].length > 0 ? (
                            <div className="row">
                              {ticketPricing[selectedCategory].map((ticket) => (
                                <div key={ticket.id} className="col-lg-4 col-md-6 col-sm-6 col-xs-12 mb-20">
                                  <div className="ticket-card">
                                    <div className="ticket-card-corner"></div>
                                    
                                    <h6 className="ticket-name">
                                      {ticket.item_name}
                                    </h6>
                                    
                                    <div className="ticket-price-badge">
                                      <span>฿{ticket.price}</span>
                                      <div className="price-shimmer"></div>
                                    </div>
                                    
                                    <div className="quantity-controls">
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const existingItem = cart.find(item => item.id === ticket.id);
                                          if (existingItem && existingItem.quantity > 1) {
                                            updateQuantity(ticket.id, existingItem.quantity - 1);
                                          } else if (existingItem) {
                                            removeFromCart(ticket.id);
                                          }
                                        }}
                                        className="quantity-btn quantity-btn-minus"
                                      >
                                        −
                                      </button>
                                      
                                      <input 
                                        type="number"
                                        min="0"
                                        max="999"
                                        className="quantity-display"
                                        value={cart.find(item => item.id === ticket.id)?.quantity || 0}
                                        onChange={(e) => {
                                          const newQuantity = parseInt(e.target.value) || 0;
                                          if (newQuantity === 0) {
                                            removeFromCart(ticket.id);
                                          } else {
                                            updateQuantity(ticket.id, newQuantity);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.target.blur();
                                          }
                                        }}
                                      />
                                      
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addToCart(ticket);
                                        }}
                                        className="quantity-btn quantity-btn-plus"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="category-empty">
                              <p>No tickets available for this category</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Cart Section for Cashier */}
              <div className="col-lg-4 col-md-4 col-sm-12">
                <div className="panel panel-default card-view">
                  <div className="cart-header">
                    <h4 className="cart-title">🛒 Cart ({cart.length})</h4>
                  </div>
                  
                  {/* Scrollable Cart Items Only */}
                  <div className="panel-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
                      {cart.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px' }}>
                          <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🛍️</div>
                          <p style={{ color: '#666' }}>Cart is empty</p>
                        </div>
                      ) : (
                        <>
                          {cart.map((item) => (
                            <div key={item.id} className="cart-item">
                              <div className="cart-item-header">
                                <div className="cart-item-name">{item.item_name}</div>
                                <button 
                                  onClick={() => removeFromCart(item.id)}
                                  className="cart-item-remove"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="cart-item-details">
                                <div>
                                  <div className="cart-item-price">฿{item.price} each</div>
                                  <div className="cart-item-total">฿{(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</div>
                                </div>
                                <div className="cart-item-controls">
                                  <button 
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="cart-control-btn"
                                  >
                                    -
                                  </button>
                                  <input 
                                    type="number"
                                    min="0"
                                    max="999"
                                    className="cart-quantity"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const newQuantity = parseInt(e.target.value) || 0;
                                      if (newQuantity === 0) {
                                        removeFromCart(item.id);
                                      } else {
                                        updateQuantity(item.id, newQuantity);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.target.blur();
                                      }
                                    }}
                                  />
                                  <button 
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="cart-control-btn"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                  </div>
                  
                  {/* Cart Total Section */}
                  {cart.length > 0 && (
                    <div className="cart-total-section" style={{
                      padding: '20px',
                      borderTop: '2px solid #e0e0e0',
                      background: '#f8f9fa'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: '#d90429'
                      }}>
                        <span>Grand Total:</span>
                        <span style={{ fontSize: '1.5rem' }}>฿{getTotalAmount()}</span>
                      </div>
                      <div style={{
                        marginTop: '8px',
                        fontSize: '0.9rem',
                        color: '#666',
                        textAlign: 'right'
                      }}>
                        {/* {cart.length} item{cart.length > 1 ? 's' : ''} in cart */}
                      </div>
                    </div>
                  )}
                  
                </div>
                
             
                
              </div>
            </div>
            <div className="row">
              <div className="col-8 col-md-8 col-sm-12">
                <div className="panel panel-default card-view">
   {/* Payment Methods Card - Shared between Cashier and Admin */}
     
                    <div className="payment-methods-container">
                      <button
                        onClick={() => handleDirectPayment('cash')}
                        disabled={cart.length === 0 || isLoadingBillingDate || !billingDate}
                        className={`material-payment-btn cash-payment ${cart.length === 0 || isLoadingBillingDate || !billingDate ? 'disabled' : ''}`}
                      >
                        <div className="payment-icon-wrapper">
                          <span className="payment-icon">💵</span>
                        </div>
                        <span className="payment-label">Cash</span>
                        <div className="payment-ripple"></div>
                      </button>
                          
                      <button
                        onClick={() => handleDirectPayment('qr')}
                        disabled={cart.length === 0 || isLoadingBillingDate || !billingDate}
                        className={`material-payment-btn qr-payment ${cart.length === 0 || isLoadingBillingDate || !billingDate ? 'disabled' : ''}`}
                      >
                        <div className="payment-icon-wrapper">
                          <span className="payment-icon">📱</span>
                        </div>
                        <span className="payment-label">QR Code</span>
                        <div className="payment-ripple"></div>
                      </button>
                      
                      <button
                        onClick={() => handleDirectPayment('card')}
                        disabled={cart.length === 0 || isLoadingBillingDate || !billingDate}
                        className={`material-payment-btn card-payment ${cart.length === 0 || isLoadingBillingDate || !billingDate ? 'disabled' : ''}`}
                      >
                        <div className="payment-icon-wrapper">
                          <span className="payment-icon">💳</span>
                        </div>
                        <span className="payment-label">Card</span>
                        <div className="payment-ripple"></div>
                      </button>
                      
                      <button
                        onClick={() => handleDirectPayment('entertainment')}
                        disabled={cart.length === 0}
                        className={`material-payment-btn entertainment-payment ${cart.length === 0 ? 'disabled' : ''}`}
                      >
                        <div className="payment-icon-wrapper">
                          <span className="payment-icon">🎭</span>
                        </div>
                        <span className="payment-label">Entertainment</span>
                        <div className="payment-ripple"></div>
                      </button>
                    </div>
                
                
              </div>
            </div>
          </div>
            
          {/* Single Floating Action Button */}
          <button
            onClick={() => setShowFloatingModal(true)}
            className="floating-action-btn"
            title="Actions Menu"
          >
            ⚙️
          </button>

          {/* Floating Actions Modal */}
          {showFloatingModal && (
            <div className="floating-modal-overlay" onClick={() => setShowFloatingModal(false)}>
              <div className="floating-modal" onClick={(e) => e.stopPropagation()}>
                <div className="floating-modal-header">
                  <h4>Actions</h4>
                  <button
                    onClick={() => setShowFloatingModal(false)}
                    className="floating-modal-close"
                  >
                    ✕
                  </button>
                </div>
                <div className="floating-modal-body">
                  <button
                    onClick={() => {
                      toggleCustomerDisplay();
                      setShowFloatingModal(false);
                    }}
                    className={`floating-modal-btn customer-display-btn ${customerDisplayWindow && !customerDisplayWindow.closed ? 'active' : ''}`}
                    title={customerDisplayWindow && !customerDisplayWindow.closed ? "Close Customer Display" : "Open Customer Display on Second Screen"}
                  >
                    <span className="floating-modal-icon">
                      {customerDisplayWindow && !customerDisplayWindow.closed ? '📺❌' : '📺'}
                    </span>
                    <span className="floating-modal-text">Customer Display</span>
                  </button>
            
                  <button
                    onClick={() => {
                      navigateToCashierSummary();
                      setShowFloatingModal(false);
                    }}
                    className="floating-modal-btn counter-close-btn"
                    title="Close Counter & View Summary"
                  >
                    <span className="floating-modal-icon">🏪</span>
                    <span className="floating-modal-text">Close Counter</span>
                  </button>
            
                  <button
                    onClick={() => {
                      navigate('/pos/bills-management');
                      setShowFloatingModal(false);
                    }}
                    
                    className="floating-modal-btn bills-management-btn"
                    title="Bills Management & Ticket Reprinting"
                  >
                    <span className="floating-modal-icon">💾</span>
                    <span className="floating-modal-text">Bills Management</span>
                  </button>
                  
                  {/* <button
                    onClick={() => {
                      navigateToSalesReport();
                      setShowFloatingModal(false);
                    }}
                    className="floating-modal-btn sales-report-btn"
                    title="Sales Report"
                  >
                    <span className="floating-modal-icon">📊</span>
                    <span className="floating-modal-text">Sales Report</span>
                  </button> */}
                  
                 
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setShowFloatingModal(false);
                    }}
                    className="floating-modal-btn logout-btn"
                    title="Logout"
                  >
                    <span className="floating-modal-icon">🚪</span>
                    <span className="floating-modal-text">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      ) : (
        // Regular Layout - With sidebar/topbar for non-cashiers
        <Layout>
      <Header title="🎡 The View Ticket Billing" />
      <ToastContainer />
      
      <div className="row">
        {/* Main Content - Categories and Tickets */}
        <div className="col-lg-8 col-md-8 col-sm-12">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h5>Loading tickets...</h5>
            </div>
          ) : (
            <>
              {/* Category Selection Cards */}
              <div className="panel panel-default card-view">
                
                <div className="panel-body" style={{ padding: '20px' }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <p>Loading categories...</p>
                    </div>
                  ) : categories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <p>No categories found. Please check your database connection.</p>
                    </div>
                  ) : (
                    <div className="categories-grid-container">
                      {categories.map((category) => (
                        <div 
                          key={category.id}
                          className={`category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                          onClick={() => handleCategorySelect(category.id)}
                        >
                          <div className="category-emoji">🎠</div>
                          <div className="category-title">{category.name}</div>
                          <div className="category-description">
                            {ticketPricing[category.id]?.length || 0} tickets
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tickets Section */}
              {selectedCategory && (
                <div className="panel panel-default card-view category-section">
                  
                  <div className="panel-body" style={{ padding: '20px' }}>
                    {ticketPricing[selectedCategory] && ticketPricing[selectedCategory].length > 0 ? (
                      <div className="row">
                        {ticketPricing[selectedCategory].map((ticket) => (
                          <div key={ticket.id} className="col-lg-4 col-md-6 col-sm-6 col-xs-12 mb-20">
                            <div className="ticket-card">
                              <div className="ticket-card-corner"></div>
                              
                              <h6 className="ticket-name">
                                {ticket.item_name}
                              </h6>
                              
                              <div className="ticket-price-badge">
                                <span>฿{ticket.price}</span>
                                <div className="price-shimmer"></div>
                              </div>
                              
                              <div className="quantity-controls">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const existingItem = cart.find(item => item.id === ticket.id);
                                    if (existingItem && existingItem.quantity > 1) {
                                      updateQuantity(ticket.id, existingItem.quantity - 1);
                                    } else if (existingItem) {
                                      removeFromCart(ticket.id);
                                    }
                                  }}
                                  className="quantity-btn quantity-btn-minus"
                                >
                                  −
                                </button>
                                
                                <span className="quantity-display">
                                  {cart.find(item => item.id === ticket.id)?.quantity || 0}
                                </span>
                                
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(ticket);
                                  }}
                                  className="quantity-btn quantity-btn-plus"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="category-empty">
                        <p>No tickets available for this category</p>
                      </div>
                    )}
                  </div>
                </div>
              )}


            </>
          )}
        </div>
          
        {/* Cart Section */}
        <div className="col-lg-4 col-md-4 col-sm-12">
          <div className="panel panel-default card-view">
            <div className="cart-header">
              <h6 className="cart-title">🛒 Cart ({cart.length})</h6>
            </div>
            <div className="panel-body" style={{ padding: '20px', maxHeight: '500px', overflowY: 'auto' }}>
                {cart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🛍️</div>
                    <p style={{ color: '#666' }}>Cart is empty</p>
                  </div>
                ) : (
                  <>
                    {cart.map((item) => (
                      <div key={item.id} className="cart-item">
                        <div className="cart-item-header">
                          <div className="cart-item-name">{item.item_name}</div>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            className="cart-item-remove"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="cart-item-details">
                          <div>
                            <div className="cart-item-price">฿{item.price} each</div>
                            <div className="cart-item-total">฿{(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</div>
                          </div>
                          <div className="cart-item-controls">
                            <button 
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="cart-control-btn"
                            >
                              -
                            </button>
                            <span className="cart-quantity">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="cart-control-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              {/* Clear Cart Button */}
                      <div className="cart-action-section">
                        <button
                          onClick={clearCart}
                          disabled={cart.length === 0}
                          className={`clear-cart-btn ${cart.length === 0 ? 'disabled' : ''}`}
                        >
                          <span>🗑️</span>
                          Clear Cart
                        </button>
                      </div>
               {/* Grand Total Section - Always visible */}
                  <div className="cart-grand-total-section">
                    <div className="grand-total-display">
                      <span className="grand-total-label">Grand Total</span>
                      <span className="grand-total-amount">฿{getTotalAmount()}</span>
                    </div>
                   
                  </div>
                  
            </div>
            
            {/* Reprint Tickets Section */}
            <div className="panel panel-default card-view" style={{ marginTop: '20px' }}>
              <div className="panel-body" style={{ padding: '20px' }}>
                <div className="reprint-section-title">
                  <h5>🎟️ Reprint Tickets</h5>
                </div>
                <div className="reprint-actions-container">
                  <button
                    onClick={handleReprintLastTicket}
                    className="material-payment-btn reprint-last"
                    style={{
                      background: 'linear-gradient(135deg, #17a2b8, #138496)',
                      color: 'white'
                    }}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">🔄</span>
                    </div>
                    <span className="payment-label">Reprint Last</span>
                    <div className="payment-ripple"></div>
                  </button>
                  
                  <button
                    onClick={handleShowReprintModal}
                    className="material-payment-btn reprint-select"
                    style={{
                      background: 'linear-gradient(135deg, #6f42c1, #563d7c)',
                      color: 'white'
                    }}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">📋</span>
                    </div>
                    <span className="payment-label">Select Ticket</span>
                    <div className="payment-ripple"></div>
                  </button>
                  
                  <button
                    onClick={() => navigate('/pos/bills-management')}
                    className="material-payment-btn database-bills"
                    style={{
                      background: 'linear-gradient(135deg, #28a745, #1e7e34)',
                      color: 'white'
                    }}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">💾</span>
                    </div>
                    <span className="payment-label">Database Bills</span>
                    <div className="payment-ripple"></div>
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Methods Card - Shared between Cashier and Admin */}
            <div className="panel panel-default card-view" style={{ marginTop: '20px' }}>
              <div className="panel-body" style={{ padding: '20px' }}>
                <div className="payment-methods-title">
                  <h5>💳 Select Payment Method</h5>
                </div>
                <div className="payment-methods-container">
                  <button
                    onClick={() => handleDirectPayment('cash')}
                    disabled={cart.length === 0}
                    className={`material-payment-btn cash-payment ${cart.length === 0 ? 'disabled' : ''}`}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">💵</span>
                    </div>
                    <span className="payment-label">Cash</span>
                    <div className="payment-ripple"></div>
                  </button>
                      
                  <button
                    onClick={() => handleDirectPayment('qr')}
                    disabled={cart.length === 0}
                    className={`material-payment-btn qr-payment ${cart.length === 0 ? 'disabled' : ''}`}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">📱</span>
                    </div>
                    <span className="payment-label">QR Code</span>
                    <div className="payment-ripple"></div>
                  </button>
                  
                  <button
                    onClick={() => handleDirectPayment('card')}
                    disabled={cart.length === 0}
                    className={`material-payment-btn card-payment ${cart.length === 0 ? 'disabled' : ''}`}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">💳</span>
                    </div>
                    <span className="payment-label">Card</span>
                    <div className="payment-ripple"></div>
                  </button>
                  
                  <button
                    onClick={() => handleDirectPayment('entertainment')}
                    disabled={cart.length === 0}
                    className={`material-payment-btn entertainment-payment ${cart.length === 0 ? 'disabled' : ''}`}
                  >
                    <div className="payment-icon-wrapper">
                      <span className="payment-icon">🎭</span>
                    </div>
                    <span className="payment-label">Entertainment</span>
                    <div className="payment-ripple"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      
        {/* Floating Customer Display Button for Regular Layout */}
        <button
          onClick={toggleCustomerDisplay}
          className={`floating-customer-display ${cart.length > 0 ? 'has-items' : ''} ${customerDisplayWindow && !customerDisplayWindow.closed ? 'active' : ''}`}
          title={customerDisplayWindow && !customerDisplayWindow.closed ? "Close Customer Display" : "Open Customer Display on Second Screen"}
        >
          {customerDisplayWindow && !customerDisplayWindow.closed ? '📺❌' : '📺'}
        </button>
        </Layout>
      )}

      {/* SCB QR Code Modal */}
      {showQRModal && qrData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          fontFamily: 'Roboto, Arial, sans-serif',
        }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '20px',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              fontWeight: 'bold',
            }} onClick={closeQRModal}>×</div>
            
            <h2 style={{
              color: '#6f42c1',
              marginBottom: '10px',
              fontSize: '1.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}>
              <span>🏦 SCB QR Payment</span>
            </h2>
            
            <p style={{
              marginBottom: '20px',
              color: '#666',
              fontSize: '1rem',
            }}>
              {qrData.isLegacy ? 
                'Scan QR code with any banking app to complete payment' :
                'Scan QR code with SCB Easy app to complete payment'
              }
            </p>
            
            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '15px',
              margin: '20px 0',
              border: '2px solid #6f42c1',
            }}>
              <div style={{
                width: '250px',
                height: '250px',
                margin: '0 auto 15px',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                borderRadius: '10px',
              }}>
                {qrData.qrRawData || qrData.qrString || qrData.deeplinkUrl ? (
                  <>
                    {/* {console.log('QR Modal - displaying QR with data:', qrData.qrRawData || qrData.qrString || qrData.deeplinkUrl)} */}
                    <QRCode 
                      value={qrData.qrRawData || qrData.qrString || qrData.deeplinkUrl} 
                      size={220}
                      level="M"
                      includeMargin={true}
                    />
                  </>
                ) : (
                  <>
                    {console.log('QR Modal - No QR data available. QR Data:', qrData)}
                    <div style={{fontSize: '14px', color: '#666'}}>No QR data available</div>
                  </>
                )}
              </div>
              
              <div style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#6f42c1',
                marginBottom: '5px',
              }}>Amount: ฿{parseFloat(getTotalAmount()).toFixed(2)}</div>
              
              <div style={{
                fontSize: '0.9rem',
                color: '#666',
              }}>Order ID: {qrOrderDetails?.orderId}</div>
              
              {qrData.isLegacy && (
                <div style={{
                  color: '#ff6b35',
                  fontSize: '12px',
                  marginTop: '10px',
                  fontWeight: 'bold',
                }}>⚠️ Using PromptPay QR Code</div>
              )}
            </div>
            
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '10px',
              padding: '15px',
              margin: '20px 0',
              color: '#856404',
            }}>
              <div style={{fontWeight: 'bold', marginBottom: '5px'}}>⏰ Payment Instructions:</div>
              <div style={{fontSize: '0.9rem', textAlign: 'left'}}>
                1. Open {qrData.isLegacy ? 'your banking' : 'SCB Easy'} app on your mobile phone<br/>
                2. Scan the QR code above<br/>
                3. Confirm the payment amount<br/>
                4. Complete the payment
              </div>
            </div>
            
            {/* Action buttons explanation */}
         
            
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              marginTop: '25px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              <button onClick={handleCheckPaymentStatus} style={{
                background: 'linear-gradient(135deg, #28a745, #20914a)',
                color: 'white',
                border: 'none',
                padding: '12px 25px',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                transition: 'transform 0.2s',
              }}>
                ✓ Check Payment
              </button>
              
              {/* Bypass verification button */}
              <button onClick={handleBypassVerification} style={{
                background: 'linear-gradient(135deg, #17a2b8, #138496)',
                color: 'white',
                border: 'none',
                padding: '12px 25px',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(23, 162, 184, 0.3)',
                transition: 'transform 0.2s',
              }}>
                ⚡ Continue Anyway
              </button>
              
              {/* Simulate button for testing */}
              {!qrData.isLegacy && (
                <button onClick={handleSimulatePayment} style={{
                  background: 'linear-gradient(135deg, #fd7e14, #e8590c)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 25px',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(253, 126, 20, 0.3)',
                  transition: 'transform 0.2s',
                }}>
                  🧪 Simulate Payment
                </button>
              )}
              
              <button onClick={closeQRModal} style={{
                background: 'linear-gradient(135deg, #6c757d, #545b62)',
                color: 'white',
                border: 'none',
                padding: '12px 25px',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                transition: 'transform 0.2s',
              }}>
                Cancel
              </button>
            </div>
            
            <div style={{
              marginTop: '20px',
              fontSize: '0.8rem',
              color: '#999',
            }}>
              Payment will timeout in 15 minutes
            </div>
          </div>
        </div>
      )}

      {/* Reprint Tickets Modal */}
      {showReprintModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          fontFamily: 'Roboto, Arial, sans-serif',
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '20px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '20px',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              fontWeight: 'bold',
            }} onClick={handleCloseReprintModal}>×</div>
            
            <h2 style={{
              color: '#6f42c1',
              marginBottom: '20px',
              fontSize: '1.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <span>🎟️ Select Ticket to Reprint</span>
            </h2>
            
            <div style={{
              flex: 1,
              overflowY: 'auto',
              paddingRight: '10px',
            }}>
              {savedTickets.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#666',
                  fontSize: '1.1rem',
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎫</div>
                  <div>No tickets available to reprint</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {savedTickets.map((ticket, index) => (
                    <div
                      key={`${ticket.ticketId}-${index}`}
                      onClick={() => handleReprintSelectedTicket(ticket)}
                      style={{
                        padding: '20px',
                        border: '2px solid #e9ecef',
                        borderRadius: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: 'linear-gradient(135deg, #f8f9fa, #ffffff)',
                        ':hover': {
                          borderColor: '#6f42c1',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(111, 66, 193, 0.15)',
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#6f42c1';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 25px rgba(111, 66, 193, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '15px',
                      }}>
                        <div>
                          <div style={{
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: '#2c3e50',
                            marginBottom: '5px',
                          }}>
                            🎟️ Ticket #{ticket.ticketId}
                            {ticket.isDatabaseTicket && (
                              <span style={{
                                fontSize: '0.8rem',
                                color: '#17a2b8',
                                marginLeft: '10px',
                                background: '#e6f7ff',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                border: '1px solid #91d5ff',
                              }}>
                                💾 Database
                              </span>
                            )}
                            {ticket.isIndividual && (
                              <span style={{
                                fontSize: '0.8rem',
                                color: '#6f42c1',
                                marginLeft: '10px',
                                background: '#f8f9ff',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                border: '1px solid #e0d9ff',
                              }}>
                                Individual {ticket.itemNumber}
                              </span>
                            )}
                          </div>
                          <div style={{
                            fontSize: '0.9rem',
                            color: '#666',
                            marginBottom: '5px',
                          }}>
                            Bill: {ticket.originalBillNumber} • {new Date(ticket.timestamp).toLocaleString()}
                          </div>
                          <div style={{
                            fontSize: '0.85rem',
                            color: '#666',
                          }}>
                            Category: {ticket.category} • Payment: {ticket.paymentMethod?.toUpperCase()}
                          </div>
                        </div>
                        <div style={{
                          textAlign: 'right',
                        }}>
                          <div style={{
                            fontSize: '1.3rem',
                            fontWeight: 'bold',
                            color: '#28a745',
                            marginBottom: '5px',
                          }}>
                            ฿{parseFloat(ticket.total).toFixed(2)}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#666',
                          }}>
                            {ticket.isDatabaseTicket ? 'Database Bill' : `${ticket.items.length} item${ticket.items.length !== 1 ? 's' : ''}`}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{
                        background: '#f8f9fa',
                        borderRadius: '10px',
                        padding: '12px',
                        fontSize: '0.85rem',
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                          {ticket.isDatabaseTicket ? 'Database Bill Details:' : 'Items:'}
                        </div>
                        {ticket.isDatabaseTicket ? (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            color: '#6c757d',
                          }}>
                            <span>Bill #{ticket.originalBillNumber}</span>
                            <span>Click to load and reprint</span>
                          </div>
                        ) : (
                          ticket.items.map((item, itemIndex) => (
                            <div key={itemIndex} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '4px',
                              color: '#6c757d',
                            }}>
                              <span>{item.item_name} × {item.quantity}</span>
                              <span>฿{parseFloat(item.total).toFixed(2)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{
              marginTop: '20px',
              textAlign: 'center',
              paddingTop: '20px',
              borderTop: '1px solid #e9ecef',
            }}>
              <button onClick={handleCloseReprintModal} style={{
                background: 'linear-gradient(135deg, #6c757d, #545b62)',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.98) 100%)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(220, 38, 38, 0.1)',
        padding: '16px 20px 14px 20px',
        textAlign: 'center',
        zIndex: 99,
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <p style={{
            margin: '0 0 6px 0',
            fontSize: '0.8rem',
            color: '#cbd5e1',
            fontWeight: '500'
          }}>
            🚀 Designed and Developed by <span style={{ color: '#fbbf24', fontWeight: '700' }}>Cloudnet Softwares Co. Ltd</span>
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            marginTop: '6px'
          }}>
            <a href="tel:+66948712350" style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#fbbf24'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
            >
              📞 +66-948712350
            </a>
            <span style={{ color: '#475569', fontSize: '0.75rem' }}>•</span>
            <a href="mailto:info@cloudnetsoftwares.com" style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              textDecoration: 'none',
              transition: 'color 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#fbbf24'}
            onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
            >
              ✉️ info@cloudnetsoftwares.com
            </a>
          </div>
        </div>
      </div>

      {/* Add bottom padding to prevent content overlap with footer */}
      <style jsx>{`
        html, body {
          margin-bottom: 80px;
        }
      `}</style>
    </>
  );
}
